import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AJUSTES_NEUTROS,
  buscarFuente,
  hashDeFuente,
  medidaMaximaMm,
  necesitaUmbral,
  rotarPoligonoMm,
  pesoDisponible,
  pixelesTrasRecorte,
  proporcionDeForma,
  recorteConProporcion,
  esSoloTrazo,
  trazadoDeForma,
  type NombreForma,
  emptyDocument,
  canonicalHash,
} from '@besbello/design-core';
import type { CanonicalDocument, Color, Layer } from '@besbello/design-core';
import type { Diagnostic } from '@besbello/rules-core';
import { api, getCsrfToken } from '../api/client.js';
import { useSession } from '../store/session.js';
import { useT } from '../i18n/index.js';
import {
  createImageLayer,
  createShapeLayer,
  createTextLayer,
  polygonBounds,
  useEditor,
  type EditorContext,
} from '../editor/document-store.js';
import { FabricCanvas } from '../editor/FabricCanvas.js';
import { esCampoDeTexto, interpretar } from '../editor/atajos.js';
import { PLANTILLAS } from '../editor/plantillas.js';
import { grupoDe } from '../editor/grupos.js';
import { arregloPara } from '../editor/arreglos.js';
import { anunciarFueraDeZona, anunciarSeleccion, describirDocumento } from '../editor/anuncios.js';
import { CARTA_PANTONE, colorPantone, contarTintas, necesitaBaseBlanca, pantallasDeSerigrafia } from '../editor/tintas.js';
import { copiar, leerCopia, olvidar, traerA, type DisenoCopiado } from '../editor/portapapeles.js';
import { acercarEn, ajustar, esVistaInicial, VISTA_INICIAL, type Vista } from '../editor/vista.js';
import { MockupPrenda } from '../editor/MockupPrenda.js';
import { SelectorFuente } from '../components/SelectorFuente.js';
import { PanelEquipo } from '../components/PanelEquipo.js';
import { capasParaZona } from '../editor/grupos.js';
import { avisosDeContraste } from '../editor/contraste.js';
import { CarruselPrendas } from '../editor/CarruselPrendas.js';
import { componerMockup, descargarCanvas, nombreDeFichero } from '../editor/descargar-mockup.js';
import { formatMm, roundMm } from '../editor/mm-px.js';
import { Badge, Banner, Button, Card, Field, NumberInput, Sheet, Spinner } from '../components/ui.js';
import {
  IconBack,
  IconCheck,
  IconDown,
  IconImage,
  IconLayers,
  IconLock,
  IconShape,
  IconSpinner,
  IconText,
  IconTrash,
  IconUnlock,
  IconUp,
  IconUndo,
  IconRedo,
  IconAlignLeft,
  IconAlignCenter,
  IconAlignRight,
  IconAlignTop,
  IconAlignMiddle,
  IconAlignBottom,
  IconDistributeH,
  IconDistributeV,
  IconZoomIn,
  IconZoomOut,
  IconZoomFit,
  IconEraser,
  IconVector,
  IconTemplate,
  IconGroup,
  IconUngroup,
  IconCopy,
} from '../components/icons.js';

/** Las formas del catálogo, en el orden en que se ofrecen. */
const FORMAS_DISPONIBLES: NombreForma[] = [
  'rectangulo',
  'rectangulo-redondeado',
  'circulo',
  'triangulo',
  'linea',
  'estrella',
  'poligono',
  'corazon',
  'flecha',
  'escudo',
  'banderola',
];

/** Lo que «las otras caras» necesita del producto para componer cada zona. */
type CatalogoCaras = {
  views: Array<{
    code: string;
    mmPerPixel: number;
    anchor: { xFrac: number; yFrac: number } | null;
    mockupProvenance: 'inherited' | 'adjusted' | 'exception';
    mockupConfidence: number;
  }>;
  colores: Array<{ id: string; imagesByView: Record<string, string | null>; dark: boolean }>;
  zones: Array<{
    zoneId: string;
    viewCode: string;
    name: string;
    polygonMm: Array<{ x: number; y: number }>;
    anchorOffsetMm?: { x: number; y: number };
  }>;
};

/**
 * LAS OTRAS CARAS DE LA PRENDA, a la vez.
 *
 * El editor trabaja UNA zona; pero una camiseta de equipo lleva el nombre
 * detrás y el escudo delante, y quien la encarga quiere ver las dos caras
 * juntas antes de pagar. Esto compone, en modo lectura, cada una de las OTRAS
 * zonas del grupo con contenido, cada una sobre la foto de SU vista (la espalda
 * sobre la foto de espalda), reutilizando el mismo mockup —con su deformación
 * por pliegues y su escala de talla—. Una cara que no cargue no tumba las demás.
 */
function OtrasCaras({
  zonas,
  designIdActual,
  colorId,
  catalogo,
  factorTalla,
}: {
  zonas: Array<{ designId: string; zoneId: string; viewCode: string; techniqueId: string; layers: number }>;
  designIdActual: string;
  colorId: string;
  catalogo: CatalogoCaras;
  factorTalla: number;
}) {
  const otras = zonas.filter((z) => z.designId !== designIdActual && z.layers > 0);
  const clave = otras.map((z) => z.designId).join(',');
  const [datos, setDatos] = useState<
    Record<string, { doc: CanonicalDocument; assetUrls: Record<string, string> }>
  >({});

  useEffect(() => {
    if (otras.length === 0) return;
    let vivo = true;
    void (async () => {
      const entradas: Record<string, { doc: CanonicalDocument; assetUrls: Record<string, string> }> = {};
      for (const z of otras) {
        try {
          const d = await api.getDesign(z.designId);
          const assets = (await fetch(`/api/designs/${z.designId}/assets`, { credentials: 'include' }).then((r) =>
            r.json(),
          )) as { items?: Array<{ checksum: string; previewUrl: string }> };
          const map: Record<string, string> = {};
          for (const a of assets.items ?? []) map[a.checksum] = a.previewUrl;
          entradas[z.designId] = { doc: d.document as CanonicalDocument, assetUrls: map };
        } catch {
          /* una cara que no carga no rompe las demás */
        }
      }
      if (vivo) setDatos(entradas);
    })();
    return () => {
      vivo = false;
    };
    // `clave` resume qué diseños hay que traer.
  }, [clave]);

  if (otras.length === 0) return null;

  return (
    <div className="mt-4" data-test="otras-caras">
      <span className="mb-1.5 block text-xs font-medium text-tinta-suave">Las otras caras de esta prenda</span>
      <div className="grid grid-cols-2 gap-3">
        {otras.map((z) => {
          const d = datos[z.designId];
          const color = catalogo.colores.find((c) => c.id === colorId);
          const imageUrl = color?.imagesByView?.[z.viewCode] ?? color?.imagesByView?.front ?? null;
          const vista = catalogo.views.find((v) => v.code === z.viewCode);
          const zona = catalogo.zones.find((zz) => zz.zoneId === z.zoneId && zz.viewCode === z.viewCode);
          if (!d || !imageUrl || !vista || !zona) {
            return (
              <div
                key={z.designId}
                className="rounded-lg border border-dashed border-borde p-4 text-center text-xs text-tinta-suave"
              >
                Cargando…
              </div>
            );
          }
          return (
            <div key={z.designId}>
              <MockupPrenda
                document={d.doc}
                zonePolygonMm={zona.polygonMm}
                zoneOffsetMm={zona.anchorOffsetMm ?? { x: 0, y: 0 }}
                encaje={{
                  imageUrl,
                  mmPerPixel: vista.mmPerPixel,
                  anchor: vista.anchor,
                  provenance: vista.mockupProvenance,
                  confidence: vista.mockupConfidence,
                }}
                assetUrls={d.assetUrls}
                dark={color?.dark ?? false}
                caraPedida={z.viewCode}
                caraMostrada={color?.imagesByView?.[z.viewCode] ? z.viewCode : 'front'}
                factorTalla={factorTalla}
                techniqueId={z.techniqueId}
              />
              <span className="mt-1 block text-center text-xs text-tinta-suave">{zona.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Cambia la tipografia de una capa dejando el `fileHash` coherente.
 *
 * El hash es de los BYTES del fichero: familia, peso y cursiva son ficheros
 * DISTINTOS. Si se cambiara el peso sin tocar el hash, el diseno guardaria que
 * se escribio con el fichero de 400 cuando en pantalla se veia el de 700, y el
 * compositor de produccion sacaria el arte con el peso equivocado.
 */
function conFuente(
  fuente: { family: string; version: string; fileHash: string; weight: number; italic: boolean },
  cambios: { family?: string; weight?: number; italic?: boolean },
) {
  const family = cambios.family ?? fuente.family;
  const catalogo = buscarFuente(family);
  // Pedirle 700 a una familia que solo trae 400 daria una negrita FALSA, y eso
  // al imprimir se ve sucio: se baja al peso mas cercano que exista de verdad.
  const weight = pesoDisponible(family, cambios.weight ?? fuente.weight);
  const italic = (cambios.italic ?? fuente.italic) && (catalogo?.italic ?? false);
  return { ...fuente, family, weight, italic, fileHash: hashDeFuente(family, weight, italic) };
}

/**
 * EDITOR.
 *
 * Dos interfaces sobre el MISMO estado (Fase 3):
 *  - MOVIL: lienzo a pantalla completa, barra inferior al alcance del pulgar y
 *    paneles en hojas deslizantes.
 *  - PC: paneles laterales fijos y valores en mm editables a mano.
 *
 * Los avisos se calculan aqui con `rules-core` —el mismo paquete del servidor—
 * y aparecen mientras se arrastra, no al pulsar "continuar".
 */
export function EditorPage() {
  const { designId = '' } = useParams();
  const [params] = useSearchParams();
  const t = useT();
  const navigate = useNavigate();

  const { document, context, selectedLayerId, validation, saveState, lastError } = useEditor();
  const init = useEditor((s) => s.init);
  const addLayer = useEditor((s) => s.addLayer);
  const updateLayer = useEditor((s) => s.updateLayer);
  const removeLayer = useEditor((s) => s.removeLayer);
  const moveLayer = useEditor((s) => s.moveLayer);
  const centerLayer = useEditor((s) => s.centerLayer);
  const save = useEditor((s) => s.save);
  const deshacer = useEditor((s) => s.deshacer);
  const rehacer = useEditor((s) => s.rehacer);
  const historial = useEditor((s) => s.historial);
  const seleccionadas = useEditor((s) => s.seleccionadas);
  const alinearSeleccion = useEditor((s) => s.alinearSeleccion);
  const agruparSeleccion = useEditor((s) => s.agruparSeleccion);
  const desagruparSeleccion = useEditor((s) => s.desagruparSeleccion);
  const seleccionarConGrupo = useEditor((s) => s.seleccionarConGrupo);
  const insertarPlantilla = useEditor((s) => s.insertarPlantilla);
  const desplazarSeleccion = useEditor((s) => s.desplazarSeleccion);
  const reemplazarCapas = useEditor((s) => s.reemplazarCapas);
  const cambiarColor = useEditor((s) => s.cambiarColor);
  const esAdmin = useSession((s) => s.isAdmin());
  const distribuirSeleccion = useEditor((s) => s.distribuirSeleccion);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assetUrls, setAssetUrls] = useState<Record<string, string>>({});
  const [sheet, setSheet] = useState<
    'layers' | 'properties' | 'diagnostics' | 'biblioteca' | 'formas' | 'plantillas' | 'equipo' | null
  >(null);
  /** Vista de trabajo (milímetros exactos) o vista sobre la prenda (mockup). */
  const [vista, setVista] = useState<'plano' | 'prenda' | 'tres-d' | 'puesto'>('plano');
  /** Mientras se pide el cambio de color al servidor. */
  const [cambiandoColor, setCambiandoColor] = useState(false);
  /** Modo de ajuste del encaje del mockup (solo catálogo). */
  const [ajustandoEncaje, setAjustandoEncaje] = useState(false);
  /** Las zonas de esta prenda y las que quedan por añadir. */
  const [grupo, setGrupo] = useState<{
    groupId: string | null;
    zonas: Array<{ designId: string; zoneId: string; viewCode: string; techniqueId: string; layers: number }>;
  } | null>(null);
  const [zonasDisponibles, setZonasDisponibles] = useState<
    Array<{ zoneId: string; name: string; viewCode: string; allowedTechniques: string[]; caja: { widthMm: number; heightMm: number } }>
  >([]);
  const [anadiendoZona, setAnadiendoZona] = useState(false);
  const [descargandoHoja, setDescargandoHoja] = useState(false);
  /** Plantilla cuyo formulario «rellena y listo» está abierto, y sus valores. */
  const [plantillaConCampos, setPlantillaConCampos] = useState<string | null>(null);
  const [valoresPlantilla, setValoresPlantilla] = useState<Record<string, string>>({});
  /**
  /** Lo que el lienzo entrega para sacar el diseño en imagen (descargas). */
  const exportarDisenoRef = useRef<(() => string | null) | null>(null);
  /** Lo último que hay que decirle a quien usa un lector de pantalla. */
  const [anuncio, setAnuncio] = useState('');
  /*
    Espejo de la hoja abierta, legible desde el manejador de teclado.

    El manejador se registra UNA vez y viviría con el valor del primer render;
    con la ref ve siempre el actual sin volver a suscribirse en cada cambio.
  */
  const hojaAbiertaRef = useRef(false);
  hojaAbiertaRef.current = sheet !== null;
  /** Enlace de solo lectura ya generado, para poder copiarlo. */
  const [enlaceCompartido, setEnlaceCompartido] = useState<string | null>(null);
  /** Encaje que se está probando, antes de guardarlo. */
  const [encajeLocal, setEncajeLocal] = useState<{ xFrac: number; yFrac: number; mmPerPixel: number } | null>(
    null,
  );
  /**
   * Ajuste del encaje de la vista «PUESTO» (la foto del modelo), aparte del de
   * la prenda plana. La foto del modelo es la medición más frágil (confianza
   * 0,35): poder recolocar el estampado arrastrándolo sobre ella, y guardarlo
   * contra la vista `puesto`, es lo que la saca de «heredada para siempre».
   */
  const [ajustandoPuesto, setAjustandoPuesto] = useState(false);
  const [encajePuestoLocal, setEncajePuestoLocal] = useState<{
    xFrac: number;
    yFrac: number;
    mmPerPixel: number;
  } | null>(null);
  /** Ultimo cambio deshecho o rehecho, para decir QUE paso. */
  const [ultimoDeshecho, setUltimoDeshecho] = useState<string | null>(null);
  /** Zoom y encuadre del lienzo de trabajo. */
  const [encuadre, setEncuadre] = useState<Vista>(VISTA_INICIAL);
  /** Diseño copiado de otra prenda, si lo hay y este está vacío. */
  const [copiaDisponible, setCopiaDisponible] = useState<DisenoCopiado | null>(null);
  /**
   * TALLA que se está previsualizando en el mockup, y cuánto escala respecto a
   * la del diseño. El factor sale de la zona que RESUELVE EL SERVIDOR para esa
   * talla —así una 4 sale al 0,61 de la M sin recalcular la tabla infantil
   * aquí— y se cachea para que cambiar de talla sea instantáneo.
   */
  const [tallaMockup, setTallaMockup] = useState<string | null>(null);
  const [escalaTallaMockup, setEscalaTallaMockup] = useState<{
    factor: number;
    offsetMm: { x: number; y: number };
  } | null>(null);
  const escalaTallaCache = useRef<Map<string, { factor: number; offsetMm: { x: number; y: number } }>>(new Map());
  /**
   * El detalle del producto que necesita «las otras caras»: para componer el
   * pecho y la espalda a la vez hay que resolver, por cada zona del grupo, la
   * foto de su vista y el color, y el polígono de su zona. Se guarda de la carga
   * para no volver a pedirlo.
   */
  const [catalogoCaras, setCatalogoCaras] = useState<CatalogoCaras | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const design = await api.getDesign(designId);
        const size = params.get('size') ?? undefined;
        const product = await api.getProduct(design.productId, { ...(size ? { size } : {}), colorId: design.colorId });
        const rules = await fetch('/api/rules/techniques', { credentials: 'include' }).then((r) => r.json());

        const zone = product.zones.find((z) => z.zoneId === design.zoneId);
        const technique = (rules.items as Array<{ techniqueId: string }>).find(
          (r) => r.techniqueId === design.techniqueId,
        );
        const color = product.colors.find((c) => c.id === design.colorId);

        if (!zone || !technique) throw new Error('faltan la zona o la regla tecnica de este diseno');
        if (cancelled) return;

        const editorContext: EditorContext = {
          designId,
          productId: design.productId,
          viewCode: zone.viewCode,
          zone: zone as never,
          technique: technique as never,
          substrate: product.substrate,
          productName: product.name,
          colorId: design.colorId,
          colorName: color?.name ?? '',
          darkGarment: color?.dark ?? false,
          colores: product.colors.map((c) => ({
            id: c.id,
            name: c.name,
            rgb: c.approximateRgb,
            // De la cara de ESTA zona: al cambiar de color trabajando la
            // espalda, la foto tiene que seguir siendo la de detrás. Si ese
            // color no la tiene, cae a la delantera y `caraMostrada` lo delata
            // para que el aviso salga al cambiar de color, no solo al cargar.
            imageUrl: c.imagesByView?.[zone.viewCode] ?? c.imagesByView?.front ?? null,
            caraMostrada: c.imagesByView?.[zone.viewCode]
              ? zone.viewCode
              : c.imagesByView?.front
                ? 'front'
                : null,
            dark: c.dark ?? false,
          })),
          sizes: [...new Set(product.variants.map((v) => v.size))],
          size: size ?? zone.size,
          /*
            La vista «puesto» —el modelo con la prenda— solo existe para los
            productos cuya colocación pasó la auditoría e2e: si no está, la
            pestaña no aparece. No es una vista de trabajo: solo mockup.
          */
          puesto: (() => {
            const v = product.views.find((x) => x.code === 'puesto');
            if (!v?.imageUrl || !v.anchor) return null;
            return {
              imageUrl: v.imageUrl,
              mmPerPixel: v.mmPerPixel,
              anchor: v.anchor,
              provenance: v.mockupProvenance,
              confidence: v.mockupConfidence,
            };
          })(),
          mockup: (() => {
            const vista = product.views.find((v) => v.code === zone.viewCode) ?? product.views[0];
            /*
              La foto de LA CARA DE ESTA ZONA y del COLOR ELEGIDO.

              Antes se cogía siempre la frontal, así que trabajando la zona de
              espalda se colocaba el diseño mirando el pecho de la prenda para
              que acabara estampado detrás. Si de esa cara no hay foto del
              color, se cae a la genérica de la vista antes que a la frontal:
              más vale la prenda en otro color que la cara equivocada.

              `caraMostrada` guarda de qué cara es la foto que finalmente se
              enseña: si acaba siendo la delantera trabajando la espalda, la UI
              lo avisa en vez de mentir.
            */
            const imageUrl =
              color?.imagesByView?.[zone.viewCode] ??
              vista?.imageUrl ??
              color?.imagesByView?.front ??
              null;
            const caraMostrada = color?.imagesByView?.[zone.viewCode]
              ? zone.viewCode
              : vista?.imageUrl
                ? (vista.code ?? null)
                : color?.imagesByView?.front
                  ? 'front'
                  : null;
            return {
              imageUrl,
              caraMostrada,
              mmPerPixel: vista?.mmPerPixel ?? 1.74,
              anchor: vista?.anchor ?? null,
              // El ancla apunta a la zona grande de la vista; ésta puede caer a
              // un lado, como el bolsillo de pecho.
              zoneOffsetMm: (zone as { anchorOffsetMm?: { x: number; y: number } }).anchorOffsetMm ?? { x: 0, y: 0 },
              provenance: vista?.mockupProvenance ?? 'inherited',
              confidence: vista?.mockupConfidence ?? 0,
            };
          })(),
        };

        init(editorContext, design.document as never, design.currentVersion);
        // Lo que «las otras caras» necesita para componer cada zona del grupo
        // sobre su propia foto: vistas (escala/ancla), colores (foto por vista)
        // y zonas (polígono) del producto, en la talla cargada.
        if (!cancelled) {
          setCatalogoCaras({
            views: product.views as CatalogoCaras['views'],
            colores: (product.colors as CatalogoCaras['colores']).map((c) => ({
              id: c.id,
              imagesByView: c.imagesByView ?? {},
              dark: c.dark ?? false,
            })),
            zones: product.zones as CatalogoCaras['zones'],
          });
        }
        // Las zonas que este producto admite, para poder ofrecer las que faltan.
        if (!cancelled) {
          setZonasDisponibles(
            product.zones.map((z) => {
              const xs = z.polygonMm.map((p: { x: number }) => p.x);
              const ys = z.polygonMm.map((p: { y: number }) => p.y);
              return {
                zoneId: z.zoneId,
                name: z.name,
                viewCode: z.viewCode,
                allowedTechniques: z.allowedTechniques,
                // La caja real de la zona: hace falta para ENCAJAR las capas al
                // copiar el diseño a otra cara.
                caja: {
                  widthMm: Math.max(...xs) - Math.min(...xs),
                  heightMm: Math.max(...ys) - Math.min(...ys),
                },
              };
            }),
          );
        }

        // URL firmadas de los assets ya usados en el documento.
        const assets = (await fetch(`/api/designs/${designId}/assets`, { credentials: 'include' }).then((r) =>
          r.json(),
        )) as { items?: Array<{ checksum: string; previewUrl: string }> };
        if (!cancelled) {
          const map: Record<string, string> = {};
          for (const asset of (assets.items ?? []) as Array<{ checksum: string; previewUrl: string }>) {
            map[asset.checksum] = asset.previewUrl;
          }
          setAssetUrls(map);
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [designId, params, init]);

  /*
    ATAJOS DE TECLADO.

    Se registran en la ventana, no en el lienzo: el foco puede estar en el panel
    de propiedades y `Ctrl+Z` tiene que seguir funcionando. Lo que sí se respeta
    es el campo de texto — ahí manda el campo, y de eso se encarga `interpretar`.
  */
  useEffect(() => {
    const alPulsar = (evento: KeyboardEvent) => {
      /*
        Con una hoja abierta, el teclado es de la HOJA.

        Escape delante de un panel abierto significa «cierra el panel», no
        «deselecciona la capa»: si el editor se lo queda, quien navega con
        teclado se queda atrapado dentro de la hoja. Y las flechas moviendo una
        capa que no se ve por detrás del panel tampoco son lo que nadie espera.
      */
      if (hojaAbiertaRef.current) return;

      const intencion = interpretar({
        key: evento.key,
        ctrlKey: evento.ctrlKey,
        metaKey: evento.metaKey,
        shiftKey: evento.shiftKey,
        enCampoDeTexto: esCampoDeTexto(evento.target),
      });
      if (!intencion) return;

      const estado = useEditor.getState();
      const capaId = estado.selectedLayerId;

      switch (intencion.tipo) {
        case 'deshacer': {
          const que = estado.deshacer();
          if (que) setUltimoDeshecho(`Deshecho: ${que}`);
          break;
        }
        case 'rehacer': {
          const que = estado.rehacer();
          if (que) setUltimoDeshecho(`Rehecho: ${que}`);
          break;
        }
        case 'borrar':
          // De atrás hacia delante: borrar por índice mientras se recorre la
          // lista hacia delante se salta capas.
          for (const id of [...estado.seleccionadas].reverse()) estado.removeLayer(id);
          break;
        case 'duplicar':
          if (capaId) estado.duplicateLayer(capaId);
          break;
        case 'deseleccionar':
          estado.select(null);
          break;
        case 'agrupar':
          estado.agruparSeleccion();
          break;
        case 'desagrupar':
          estado.desagruparSeleccion();
          break;
        case 'mover':
          // Mueve TODA la selección, no solo la capa que manda en el panel: si
          // hay tres elegidas y las flechas movieran una, la relación entre
          // ellas se rompería sin que el usuario lo pidiera.
          estado.desplazarSeleccion(intencion.dx, intencion.dy);
          break;
        default:
          return;
      }
      // Solo se traga el evento si se ha hecho algo: si no, se rompen los
      // atajos del navegador sin motivo.
      evento.preventDefault();
    };

    window.addEventListener('keydown', alPulsar);
    return () => window.removeEventListener('keydown', alPulsar);
  }, []);

  /** El aviso de «deshecho: …» se retira solo. */
  useEffect(() => {
    if (!ultimoDeshecho) return;
    const t = setTimeout(() => setUltimoDeshecho(null), 2600);
    return () => clearTimeout(t);
  }, [ultimoDeshecho]);

  /**
   * Coloca en el diseño una imagen que YA está subida.
   *
   * Se comparte entre la subida nueva y la biblioteca: una peña que encarga
   * camisetas en marzo y sudaderas en octubre sube el mismo escudo dos veces, y
   * el almacén ya guarda cada asset con su checksum, así que reutilizarlo sale
   * gratis y sin duplicar nada.
   */
  const colocarImagen = useCallback(
    (asset: { checksum: string; widthPx: number; heightPx: number; previewUrl: string }) => {
      if (!context) return;
      setAssetUrls((current) => ({ ...current, [asset.checksum]: asset.previewUrl }));
      const bounds = polygonBounds(context.zone.polygonMm);
      addLayer(
        createImageLayer({
          assetChecksum: asset.checksum,
          widthPx: asset.widthPx,
          heightPx: asset.heightPx,
          zone: bounds,
          minDpi: context.technique.minEffectiveDpi,
        }),
      );
    },
    [addLayer, context],
  );

  const onUpload = useCallback(
    async (file: File) => {
      if (!context) return;
      try {
        colocarImagen(await api.uploadAsset(file));
      } catch (err) {
        setError((err as Error).message);
      }
    },
    [colocarImagen, context],
  );

  /*
    ¿Hay un diseño copiado de otra prenda?

    Solo se ofrece cuando este está VACÍO. Ofrecerlo con trabajo ya hecho sería
    invitar a pisarlo, y el aviso aparecería en cada diseño nuevo de la sesión
    como un estorbo.
  */
  useEffect(() => {
    if (loading || document.layers.length > 0) {
      setCopiaDisponible(null);
      return;
    }
    setCopiaDisponible(leerCopia());
  }, [loading, document.layers.length]);

  /*
    QUÉ SE DICE Y CUÁNDO.

    Al cambiar de capa, la capa entera —quién es, dónde está y cuánto mide—.
    Mientras se mueve, solo lo que ha cambiado, y el aviso de que se sale, que es
    el único que altera lo que va a llegar impreso. Decirlo todo cada vez
    convierte el lector de pantalla en ruido.
  */
  useEffect(() => {
    const capa = document.layers.find((l) => l.id === selectedLayerId);
    if (!capa) {
      setAnuncio(describirDocumento(document.layers, validation.counts.error));
      return;
    }
    const indice = document.layers.findIndex((l) => l.id === capa.id);
    const fuera = context
      ? anunciarFueraDeZona(capa, {
          widthMm: document.canvasMm.width,
          heightMm: document.canvasMm.height,
        })
      : null;
    setAnuncio([anunciarSeleccion(capa, indice, document.layers.length), fuera].filter(Boolean).join('. '));
  }, [selectedLayerId, document, validation.counts.error, context]);

  /*
    Las zonas se piden APARTE del diseño.

    Podrían venir en el mismo `getDesign`, pero eso cargaría la respuesta de
    todos los diseños del sistema con datos que solo hacen falta cuando el
    producto tiene más de una zona, que no es el caso general.

    Depende del NÚMERO de capas, no del documento entero. Con el documento en
    las dependencias, esto pedía las zonas al servidor en cada tecla que se
    escribiera en un texto: decenas de peticiones por minuto para redibujar unas
    pestañas que solo cambian cuando una zona pasa de vacía a tener algo.
  */
  useEffect(() => {
    let vivo = true;
    void api
      .zonasDelDiseno(designId)
      .then((r) => {
        if (vivo) setGrupo(r);
      })
      .catch(() => {
        // No poder pintar las pestañas no puede impedir diseñar.
      });
    return () => {
      vivo = false;
    };
  }, [designId, document.layers.length]);

  if (loading) return <Spinner label={t('app.loading')} />;
  if (error) {
    return (
      <div className="p-4">
        <Banner tone="error" title={t('app.error')}>{error}</Banner>
      </div>
    );
  }
  if (!context) return null;

  const hayQueDeshacer = historial.pasados.length > 0;
  const hayQueRehacer = historial.futuros.length > 0;
  const zoneBounds = polygonBounds(context.zone.polygonMm);
  const selected = document.layers.find((l) => l.id === selectedLayerId) ?? null;
  const errorLayers = validation.diagnostics.filter((d) => d.severity === 'error' && d.layerId).map((d) => d.layerId!);

  const addText = () =>
    addLayer(
      createTextLayer({
        text: 'BESBELLO',
        // En prenda oscura, el texto negro por defecto no se veria.
        color: context.darkGarment
          ? { type: 'rgb', r: 255, g: 255, b: 255 }
          : { type: 'rgb', r: 17, g: 17, b: 17 },
        zone: zoneBounds,
      }),
    );

  /*
    Tinta por defecto: blanca en prenda oscura y negra en prenda clara.

    Es la única que se lee en las dos, y empezar con una forma invisible sobre
    la prenda haría creer que no se ha añadido nada.
  */
  /*
    El color REAL de la prenda elegida.

    Estaba puesto a mano como «gris claro o casi negro» según fuera clara u
    oscura, que sirve para juzgar el contraste en el lienzo plano pero no para
    enseñar la prenda. El catálogo trae el color aproximado de cada uno.
  */
  const colorDePrenda = context.colores.find((c) => c.id === context.colorId)?.rgb ??
    (context.darkGarment ? { r: 34, g: 34, b: 34 } : { r: 244, g: 243, b: 240 });

  const tintaPorDefecto = context.darkGarment
    ? ({ type: 'rgb', r: 255, g: 255, b: 255 } as const)
    : ({ type: 'rgb', r: 17, g: 17, b: 17 } as const);

  /**
   * Cambia el color de la prenda.
   *
   * Se avisa al servidor y se actualiza aquí. El orden importa: si se
   * actualizara primero la pantalla y el servidor fallara, el cliente estaría
   * diseñando sobre un color que su pedido no lleva.
   */
  const cambiarColorPrenda = async (color: {
    id: string;
    name: string;
    dark: boolean;
    imageUrl: string | null;
    caraMostrada: string | null;
  }) => {
    if (!context || color.id === context.colorId) return;
    setCambiandoColor(true);
    try {
      await api.cambiarColorDiseno(designId, color.id);
      cambiarColor(color);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCambiandoColor(false);
    }
  };

  /*
    PROBAR LA TALLA en el mockup.

    El diseño se autoría una vez, pero el área de marcaje NO es la misma en una
    4 que en una 3XL: el estampado sale más pequeño (y más arriba) en las
    pequeñas. El factor no se recalcula aquí —sería duplicar la tabla infantil
    del servidor y arriesgarse a que discrepen— sino que se pide al servidor la
    zona RESUELTA para esa talla y se compara su ancho con el de la talla base.
    Se cachea por talla; si el servidor falla, se queda en la base y no rompe
    nada (es una vista previa).
  */
  const anchoZonaMm = (poly: { x: number }[]) => {
    const xs = poly.map((p) => p.x);
    return Math.max(...xs) - Math.min(...xs);
  };
  const probarTallaMockup = async (size: string) => {
    setTallaMockup(size);
    if (!context) return;
    if (size === context.size) {
      setEscalaTallaMockup(null);
      return;
    }
    const cacheado = escalaTallaCache.current.get(size);
    if (cacheado) {
      setEscalaTallaMockup(cacheado);
      return;
    }
    try {
      const detalle = await api.getProduct(context.productId, { size, colorId: context.colorId });
      const zonaS = detalle.zones.find(
        (z) => z.zoneId === context.zone.zoneId && z.viewCode === context.viewCode,
      );
      if (!zonaS) return;
      const anchoBase = anchoZonaMm(context.zone.polygonMm);
      const factor = anchoBase > 0 ? anchoZonaMm(zonaS.polygonMm) / anchoBase : 1;
      const escala = { factor, offsetMm: zonaS.anchorOffsetMm ?? context.mockup.zoneOffsetMm };
      escalaTallaCache.current.set(size, escala);
      setEscalaTallaMockup(escala);
    } catch {
      /* vista previa: si no se puede resolver la talla, se ve la base */
    }
  };

  /**
   * Descarga el mockup para enseñárselo a alguien.
   *
   * Quien encarga veinte camisetas para una peña lo decide en grupo. Sin esta
   * imagen hace una captura de pantalla y la comparte igual, pero con la
   * interfaz del editor dentro y sin la advertencia de que es orientativo.
   */
  const descargarVistaPrenda = async () => {
    if (!context?.mockup.imageUrl || !context.mockup.anchor) return;
    try {
      const foto = new Image();
      foto.crossOrigin = 'anonymous';
      await new Promise<void>((ok, fallo) => {
        foto.onload = () => ok();
        foto.onerror = () => fallo(new Error(t('editor.downloadFailed')));
        foto.src = context.mockup.imageUrl as string;
      });

      // La zona sobre la foto, con la MISMA cuenta que hace el mockup: si se
      // calculara aquí por separado, un día dejarían de coincidir.
      const anchoFotoOriginalPx = 600;
      const fraccionPorMm = 1 / (context.mockup.mmPerPixel * anchoFotoOriginalPx);
      const anchoFrac = zoneBounds.widthMm * fraccionPorMm;
      const altoFrac = zoneBounds.heightMm * fraccionPorMm;

      const lienzoDelEditor = window.document.querySelector<HTMLCanvasElement>(
        '.lienzo-tactil canvas.lower-canvas',
      );

      const compuesto = componerMockup({
        foto,
        diseno: lienzoDelEditor,
        zona: {
          xFrac: context.mockup.anchor.xFrac - anchoFrac / 2,
          yFrac: context.mockup.anchor.yFrac,
          anchoFrac,
          altoFrac,
        },
        titulo: `${context.productName} · ${context.colorName}`,
        aviso: t('editor.mockupDisclaimer'),
      });

      await descargarCanvas(compuesto, nombreDeFichero(context.productName, context.colorName));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  /**
   * Descarga la HOJA DE APROBACIÓN en PDF.
   *
   * La imagen es EXACTAMENTE lo que el cliente ve (el mismo componerMockup de
   * la descarga); las medidas en milímetros las pone el SERVIDOR desde el
   * documento guardado, que es la fuente de verdad. Con línea de firma: una
   * hoja firmada corta discusiones después.
   */
  const descargarHojaPrueba = async () => {
    if (!context?.mockup.imageUrl || !context.mockup.anchor) return;
    setDescargandoHoja(true);
    try {
      const foto = new Image();
      foto.crossOrigin = 'anonymous';
      await new Promise<void>((ok, fallo) => {
        foto.onload = () => ok();
        foto.onerror = () => fallo(new Error(t('editor.downloadFailed')));
        foto.src = context.mockup.imageUrl as string;
      });

      const anchoFotoOriginalPx = 600;
      const fraccionPorMm = 1 / (context.mockup.mmPerPixel * anchoFotoOriginalPx);
      const anchoFrac = zoneBounds.widthMm * fraccionPorMm;
      const altoFrac = zoneBounds.heightMm * fraccionPorMm;
      const lienzoDelEditor = window.document.querySelector<HTMLCanvasElement>(
        '.lienzo-tactil canvas.lower-canvas',
      );
      const compuesto = componerMockup({
        foto,
        diseno: lienzoDelEditor,
        zona: {
          xFrac: context.mockup.anchor.xFrac - anchoFrac / 2,
          yFrac: context.mockup.anchor.yFrac,
          anchoFrac,
          altoFrac,
        },
        titulo: `${context.productName} · ${context.colorName}`,
        aviso: t('editor.mockupDisclaimer'),
      });

      const pngBase64 = compuesto.toDataURL('image/png').split(',')[1] ?? '';
      const respuesta = await fetch(`/api/designs/${designId}/hoja-prueba`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json', 'x-csrf-token': getCsrfToken() ?? '' },
        body: JSON.stringify({ imagenes: [{ designId, pngBase64 }] }),
      });
      if (!respuesta.ok) throw new Error(t('editor.downloadFailed'));
      const blob = await respuesta.blob();
      const url = URL.createObjectURL(blob);
      const enlace = window.document.createElement('a');
      enlace.href = url;
      enlace.download = 'hoja-prueba.pdf';
      enlace.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDescargandoHoja(false);
    }
  };

  /**
   * Guarda el encaje ajustado a ojo.
   *
   * Es un dato del CATÁLOGO: afecta a todos los diseños de ese producto, no
   * solo al que se está viendo. Por eso lo pide una cuenta con permiso y por
   * eso la confianza pasa a 1 —alguien lo ha mirado en ESTA prenda— en vez de
   * seguir heredado de la familia con un 50 %.
   */
  const guardarEncaje = async () => {
    if (!context || !encajeLocal) return;
    try {
      await api.guardarEncajeMockup(context.productId, context.viewCode, {
        anchor: { xFrac: encajeLocal.xFrac, yFrac: encajeLocal.yFrac },
        mmPerPixel: encajeLocal.mmPerPixel,
      });
      setAjustandoEncaje(false);
      setUltimoDeshecho(t('editor.fitSaved'));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  /** Guardar el encaje de la FOTO DEL MODELO (vista «puesto»), aparte. */
  const guardarEncajePuesto = async () => {
    if (!context?.puesto || !encajePuestoLocal) return;
    try {
      await api.guardarEncajeMockup(context.productId, 'puesto', {
        anchor: { xFrac: encajePuestoLocal.xFrac, yFrac: encajePuestoLocal.yFrac },
        mmPerPixel: encajePuestoLocal.mmPerPixel,
      });
      setAjustandoPuesto(false);
      setUltimoDeshecho(t('editor.fitSaved'));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  /**
   * Enlace para que lo aprueben.
   *
   * Se enseña en un campo de solo lectura en vez de copiarlo al portapapeles
   * sin más: el portapapeles no siempre está permitido —hace falta un gesto del
   * usuario y a veces ni con eso—, y un botón que dice «copiado» sin haber
   * copiado nada es peor que no tenerlo.
   */
  const compartir = async () => {
    try {
      const r = await api.compartirDiseno(designId, 7);
      setEnlaceCompartido(r.url);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  /**
   * Añade otra zona a esta prenda.
   *
   * Se navega al diseño nuevo en cuanto existe: el cliente acaba de pedir la
   * espalda y lo que quiere es ponerse a diseñarla, no volver a pulsar.
   */
  const anadirZona = async (zona: { zoneId: string; viewCode: string; allowedTechniques: string[] }) => {
    setAnadiendoZona(true);
    try {
      const tecnica = zona.allowedTechniques.includes(context.technique.techniqueId)
        ? context.technique.techniqueId
        : zona.allowedTechniques[0];
      if (!tecnica) return;
      const nuevo = await api.anadirZona(designId, {
        zoneId: zona.zoneId,
        viewCode: zona.viewCode,
        techniqueId: tecnica as 'dtf',
      });
      navigate(`/editor/${nuevo.id}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setAnadiendoZona(false);
    }
  };

  /**
   * Copia ESTE diseño a otra zona de la prenda.
   *
   * El mismo escudo delante y detrás es el encargo típico, y hasta ahora había
   * que construirlo dos veces. Pecho y espalda son la misma caja a propósito
   * —ese fue el motivo declarado de igualarlas— así que la copia es 1:1; hacia
   * una caja distinta (el bolsillo), `llevarAZona` encaja y centra, que es
   * exactamente lo que ya hacen las plantillas.
   *
   * El servidor crea el diseño con documento vacío (comprobado en su handler) y
   * la primera versión guardada encima lleva las capas copiadas: el autoguardado
   * y el control optimista son los de siempre, sin caminos nuevos.
   */
  const copiarAZona = async (zona: {
    zoneId: string;
    viewCode: string;
    allowedTechniques: string[];
    caja: { widthMm: number; heightMm: number };
  }) => {
    setAnadiendoZona(true);
    try {
      const tecnica = zona.allowedTechniques.includes(context.technique.techniqueId)
        ? context.technique.techniqueId
        : zona.allowedTechniques[0];
      if (!tecnica) return;
      const nuevo = await api.anadirZona(designId, {
        zoneId: zona.zoneId,
        viewCode: zona.viewCode,
        techniqueId: tecnica as 'dtf',
      });

      const capas = capasParaZona(
        document.layers,
        { widthMm: document.canvasMm.width, heightMm: document.canvasMm.height },
        zona.caja,
      );
      const doc = { ...emptyDocument(zona.zoneId, zona.caja.widthMm, zona.caja.heightMm), layers: capas };
      await api.saveDesignVersion(nuevo.id, {
        baseVersion: nuevo.currentVersion,
        document: doc,
        clientKey: canonicalHash(doc).slice(7, 39),
      });
      navigate(`/editor/${nuevo.id}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setAnadiendoZona(false);
    }
  };

  /**
   * Gira la zona de marcaje un cuarto de vuelta.
   *
   * La zona viene del patrón con una orientación, y no siempre es la que hace
   * falta: un bolsillo admite un estampado apaisado y una espalda puede querer
   * el arte en vertical. Hasta ahora eso obligaba a reescribir el polígono a
   * mano, producto por producto.
   *
   * Cambia el CATÁLOGO, no este diseño: por eso solo lo ve quien administra, y
   * por eso se recarga la página al terminar —el lienzo, la validación y el
   * mockup se dibujan todos a partir de la zona, y volver a montarlos es más
   * honesto que remendar cada uno por su cuenta.
   */
  const girarZona = async () => {
    if (!context) return;
    try {
      await api.guardarZona(context.productId, context.zone.zoneId, {
        polygonMm: rotarPoligonoMm(context.zone.polygonMm, 90),
        viewCode: context.viewCode,
        note: 'girada un cuarto de vuelta desde el editor',
      });
      window.location.reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const addShape = () => setSheet('formas');

  return (
    <div className="flex h-[100dvh] flex-col">
      <header className="safe-top flex items-center gap-2 border-b border-borde bg-white px-3 py-2">
        <Link to={`/productos/${''}`} onClick={(e) => { e.preventDefault(); navigate(-1); }} className="toque rounded-lg p-2">
          <IconBack />
          <span className="sr-only">{t('app.back')}</span>
        </Link>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{context.productName}</p>
          <p className="truncate text-xs text-tinta-suave">
            {context.colorName} · {context.zone.name} · {formatMm(zoneBounds.widthMm)} × {formatMm(zoneBounds.heightMm)}
          </p>
        </div>
        {/* Deshacer y rehacer. Antes no existían: borrar una capa era definitivo. */}
        <div className="flex items-center gap-1">
          <button
            data-test="deshacer"
            onClick={() => {
              const que = deshacer();
              if (que) setUltimoDeshecho(`Deshecho: ${que}`);
            }}
            disabled={!hayQueDeshacer}
            title="Deshacer (Ctrl+Z)"
            className="toque rounded-lg p-2 disabled:opacity-30"
          >
            <IconUndo size={18} />
            <span className="sr-only">{t('editor.undo')}</span>
          </button>
          <button
            data-test="rehacer"
            onClick={() => {
              const que = rehacer();
              if (que) setUltimoDeshecho(`Rehecho: ${que}`);
            }}
            disabled={!hayQueRehacer}
            title="Rehacer (Ctrl+Shift+Z)"
            className="toque rounded-lg p-2 disabled:opacity-30"
          >
            <IconRedo size={18} />
            <span className="sr-only">{t('editor.redo')}</span>
          </button>
        </div>

        <SaveIndicator state={saveState} onRetry={() => void save()} />
        <Button
          data-test="continuar"
          onClick={() => navigate(`/checkout/${designId}?size=${encodeURIComponent(context.size)}`)}
          disabled={validation.counts.error > 0 || document.layers.length === 0}
        >
          {t('editor.checkout')}
        </Button>
      </header>

      {saveState === 'conflict' && (
        <div className="p-3">
          <Banner tone="warning" title={t('editor.conflict')}>
            {t('editor.conflictHelp')}
            <div className="mt-2">
              <Button variant="secondary" onClick={() => window.location.reload()}>
                {t('app.retry')}
              </Button>
            </div>
          </Banner>
        </div>
      )}

      {/*
        «LO MISMO PERO EN SUDADERA».

        Solo aparece con el diseño vacío: ofrecerlo con trabajo ya hecho sería
        invitar a pisarlo. El diseño se reescala a esta zona por el lado más
        apretado, sin deformar y sin ampliar lo que ya cabía.
      */}
      {copiaDisponible && (
        <div className="border-b border-borde bg-fondo-suave px-3 py-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="flex-1 text-xs">
              {t('editor.bringDesign')
                .replace('{producto}', copiaDisponible.productName)
                .replace('{zona}', copiaDisponible.zoneName)}
            </p>
            <Button
              data-test="traer-diseno"
              onClick={() => {
                const { layers } = traerA(copiaDisponible, zoneBounds);
                reemplazarCapas(layers, t('editor.bringDesignDone'));
                olvidar();
                setCopiaDisponible(null);
              }}
            >
              {t('editor.bringDesignAction')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                olvidar();
                setCopiaDisponible(null);
              }}
            >
              {t('app.dismiss')}
            </Button>
          </div>
        </div>
      )}

      {/*
        TIRA DE COLORES.

        Ver el mismo diseño en negro y en blanco es la comprobación más útil que
        existe, y hasta hoy obligaba a volver a la ficha del producto y empezar
        de cero. El color NO entra en el documento —es el soporte, no una capa—
        pero sí cambia la validación: una tinta blanca sobre prenda blanca no se
        ve, y el aviso tiene que aparecer al momento.
      */}
      {vista !== 'plano' && context.colores.length > 1 && (
        <div className="border-b border-borde bg-white px-3 py-2">
          <ul className="flex flex-wrap items-center gap-1.5">
            {context.colores.map((color) => (
              <li key={color.id}>
                <button
                  type="button"
                  data-test="color-prenda"
                  aria-pressed={color.id === context.colorId}
                  aria-label={color.name}
                  title={color.name}
                  disabled={cambiandoColor}
                  onClick={() => void cambiarColorPrenda(color)}
                  className={`toque h-8 w-8 rounded-full border-2 disabled:opacity-50 ${
                    color.id === context.colorId ? 'border-marca' : 'border-borde'
                  }`}
                  style={{ backgroundColor: `rgb(${color.rgb.r},${color.rgb.g},${color.rgb.b})` }}
                />
              </li>
            ))}
            <li className="ml-1 text-xs text-tinta-suave">{context.colorName}</li>
          </ul>
        </div>
      )}

      {/*
        LAS ZONAS DE ESTA PRENDA.

        Solo aparece si el producto tiene más de una: en una taza, unas pestañas
        con una sola zona son ruido. Cada zona es un diseño con su documento —el
        taller imprime una pasada por zona— y estas pestañas solo cambian de
        cuál se está editando.
      */}
      {zonasDisponibles.length > 1 && (
        <nav
          data-test="pestanas-zona"
          aria-label={t('editor.zones')}
          className="flex items-center gap-1 overflow-x-auto border-b border-borde bg-white px-3 py-1.5"
        >
          {grupo?.zonas.map((zona) => (
            <button
              key={zona.designId}
              data-test="zona-pestana"
              aria-current={zona.designId === designId ? 'page' : undefined}
              onClick={() => {
                if (zona.designId !== designId) navigate(`/editor/${zona.designId}`);
              }}
              className={`toque shrink-0 rounded-full px-3 py-1 text-xs ${
                zona.designId === designId ? 'bg-marca text-white' : 'border border-borde text-tinta-suave'
              }`}
            >
              {zona.zoneId}
              {/* Un punto para las zonas que aún están vacías: el cliente añade
                  la espalda, se va al pecho y luego no recuerda cuál dejó a medias. */}
              {zona.layers === 0 && <span className="ml-1 opacity-60">·</span>}
            </button>
          ))}

          {/* Las zonas del producto que todavía no están en el pedido. */}
          {zonasDisponibles
            .filter((z) => !(grupo?.zonas ?? []).some((usada) => usada.zoneId === z.zoneId))
            .map((zona) => (
              <span key={zona.zoneId} className="flex shrink-0 items-center gap-1">
                <button
                  data-test="anadir-zona"
                  disabled={anadiendoZona}
                  onClick={() => void anadirZona(zona)}
                  className="toque shrink-0 rounded-full border border-dashed border-borde px-3 py-1 text-xs text-tinta-suave disabled:opacity-50"
                >
                  + {zona.name}
                </button>
                {/* Copiar lo que ya hay a esa zona: solo tiene sentido con algo
                    que copiar. */}
                {document.layers.length > 0 && (
                  <button
                    data-test="copiar-a-zona"
                    disabled={anadiendoZona}
                    onClick={() => void copiarAZona(zona)}
                    className="toque shrink-0 rounded-full border border-dashed border-borde px-3 py-1 text-xs text-tinta-suave disabled:opacity-50"
                  >
                    {t('editor.copyToZone', { zona: zona.name })}
                  </button>
                )}
              </span>
            ))}
        </nav>
      )}

      {/*
        LO QUE PASA EN EL LIENZO, DICHO EN VOZ ALTA.

        Un <canvas> es una caja negra para un lector de pantalla: dentro no hay
        nada que leer. Las medidas en milímetros ya están calculadas —son las
        mismas que se validan y las mismas que llegan al taller— así que
        decirlas sale gratis.

        `aria-live="polite"` y no `assertive`: se espera a que el lector termine
        lo que estaba diciendo. Interrumpir en cada pulsación de flecha es lo que
        hace que la gente apague el lector.
      */}
      <p role="status" aria-live="polite" className="sr-only" data-test="anuncio-lienzo">
        {anuncio}
      </p>

      <div className="flex min-h-0 flex-1">
        {/* --- PC: herramientas y capas a la izquierda ------------------------ */}
        <aside className="hidden w-64 shrink-0 space-y-3 overflow-y-auto border-r border-borde p-3 lg:block">
          {/*
            Las herramientas vivian SOLO en la barra inferior, que es `lg:hidden`:
            en un ordenador el editor se abria sin ninguna forma de anadir texto,
            imagen o forma. Aqui estan las mismas acciones para pantalla grande.
          */}
          <div className="grid grid-cols-3 gap-2">
            <ToolButton
              test="anadir-imagen"
              icon={<IconImage />}
              label={t('editor.addImage')}
              onClick={() => fileRef.current?.click()}
            />
            <ToolButton test="anadir-texto" icon={<IconText />} label={t('editor.addText')} onClick={addText} />
            <ToolButton test="anadir-forma" icon={<IconShape />} label={t('editor.addShape')} onClick={addShape} />
            {/* Las imagenes ya subidas: buscar otra vez el fichero en el movil
                es la parte mas tediosa de repetir un encargo. */}
            <ToolButton
              test="mis-imagenes"
              icon={<IconLayers />}
              label={t('editor.myImages')}
              onClick={() => setSheet('biblioteca')}
            />
            <ToolButton
              test="plantillas"
              icon={<IconTemplate />}
              label={t('editor.templates')}
              onClick={() => setSheet('plantillas')}
            />
            <ToolButton
              test="equipo"
              icon={<IconLayers />}
              label={t('equipo.titulo')}
              onClick={() => setSheet('equipo')}
            />
            {/* Copiar a otra prenda: se guarda aquí y se ofrece al abrir el
                editor de la otra, que es cuando hace falta. */}
            <ToolButton
              test="copiar-diseno"
              icon={<IconCopy />}
              label={t('editor.copyDesign')}
              onClick={() => {
                copiar({
                  productName: context.productName,
                  zoneName: context.zone.name,
                  zona: zoneBounds,
                  layers: document.layers,
                });
                setUltimoDeshecho(t('editor.copyDesignDone'));
              }}
            />
          </div>

          <LayersPanel
            layers={document.layers}
            selectedId={selectedLayerId}
            onSelect={seleccionarConGrupo}
            onMove={moveLayer}
            onRemove={removeLayer}
            onToggleLock={(layer) => updateLayer(layer.id, { locked: !layer.locked } as Partial<Layer>)}
          />
        </aside>

        <div className="relative min-w-0 flex-1 bg-papel-suave">
          {/*
            Dos vistas del MISMO documento: la de trabajo, donde cada milímetro
            cae donde tiene que caer, y la de la prenda, que responde a la
            pregunta que se hace todo el mundo antes de pagar.
          */}
          <div className="absolute left-1/2 top-3 z-10 flex -translate-x-1/2 gap-1 rounded-full border border-borde bg-white/95 p-1 shadow-sm backdrop-blur">
            <button
              data-test="ver-plano"
              onClick={() => setVista('plano')}
              aria-pressed={vista === 'plano'}
              className={`rounded-full px-3 py-1.5 text-xs font-medium ${
                vista === 'plano' ? 'bg-marca text-white' : 'text-tinta-suave'
              }`}
            >
              {t('editor.viewFlat')}
            </button>
            <button
              data-test="ver-prenda"
              onClick={() => setVista('prenda')}
              aria-pressed={vista === 'prenda'}
              className={`rounded-full px-3 py-1.5 text-xs font-medium ${
                vista === 'prenda' ? 'bg-marca text-white' : 'text-tinta-suave'
              }`}
            >
              {t('editor.viewGarment')}
            </button>
            {context.puesto && (
              <button
                data-test="ver-puesto"
                onClick={() => setVista('puesto')}
                aria-pressed={vista === 'puesto'}
                className={`rounded-full px-3 py-1.5 text-xs font-medium ${
                  vista === 'puesto' ? 'bg-marca text-white' : 'text-tinta-suave'
                }`}
              >
                {t('editor.viewWorn')}
              </button>
            )}
            {/*
              TRES DIMENSIONES.

              Estaba escrito desde la Fase 7 —con su rama de salida propia y su
              aviso de que no sustituye a una prueba de color— pero no estaba
              montado en ninguna pantalla. Es de las cosas más baratas que
              quedaban: el componente ya existía entero.
            */}
            <button
              data-test="ver-3d"
              onClick={() => setVista('tres-d')}
              aria-pressed={vista === 'tres-d'}
              className={`rounded-full px-3 py-1.5 text-xs font-medium ${
                vista === 'tres-d' ? 'bg-marca text-white' : 'text-tinta-suave'
              }`}
            >
              {t('editor.view3d')}
            </button>
          </div>

          {/*
            ALINEAR Y REPARTIR.

            Solo aparece cuando hay algo seleccionado: una barra de herramientas
            permanente con todos los botones apagados ocupa sitio y no dice nada.
            Con una capa se alinea respecto a la zona; con varias, entre ellas.
          */}
          {vista === 'plano' && seleccionadas.length > 0 && (
            <div
              data-test="barra-alinear"
              className="absolute left-3 top-16 z-10 flex flex-col gap-1 rounded-xl border border-borde bg-white/95 p-1 shadow-sm backdrop-blur"
            >
              <div className="flex gap-0.5">
                <BotonHerramienta
                  test="alinear-izquierda"
                  etiqueta={`${t('editor.alignTools')} · ${t('editor.align_left')}`}
                  onClick={() => alinearSeleccion('izquierda')}
                >
                  <IconAlignLeft size={16} />
                </BotonHerramienta>
                <BotonHerramienta
                  test="alinear-centro-h"
                  etiqueta={`${t('editor.alignTools')} · ${t('editor.align_center')}`}
                  onClick={() => alinearSeleccion('centro-h')}
                >
                  <IconAlignCenter size={16} />
                </BotonHerramienta>
                <BotonHerramienta
                  test="alinear-derecha"
                  etiqueta={`${t('editor.alignTools')} · ${t('editor.align_right')}`}
                  onClick={() => alinearSeleccion('derecha')}
                >
                  <IconAlignRight size={16} />
                </BotonHerramienta>
              </div>
              <div className="flex gap-0.5">
                <BotonHerramienta
                  test="alinear-arriba"
                  etiqueta={`${t('editor.alignTools')} · ${t('editor.alignTop')}`}
                  onClick={() => alinearSeleccion('arriba')}
                >
                  <IconAlignTop size={16} />
                </BotonHerramienta>
                <BotonHerramienta
                  test="alinear-centro-v"
                  etiqueta={`${t('editor.alignTools')} · ${t('editor.alignMiddle')}`}
                  onClick={() => alinearSeleccion('centro-v')}
                >
                  <IconAlignMiddle size={16} />
                </BotonHerramienta>
                <BotonHerramienta
                  test="alinear-abajo"
                  etiqueta={`${t('editor.alignTools')} · ${t('editor.alignBottom')}`}
                  onClick={() => alinearSeleccion('abajo')}
                >
                  <IconAlignBottom size={16} />
                </BotonHerramienta>
              </div>
              {/* Repartir necesita TRES: con dos, el hueco lo definen los extremos
                  y no hay nada que calcular. */}
              {seleccionadas.length >= 3 && (
                <div className="flex gap-0.5 border-t border-borde pt-1">
                  <BotonHerramienta
                    test="repartir-h"
                    etiqueta={t('editor.distributeH')}
                    onClick={() => distribuirSeleccion('x')}
                  >
                    <IconDistributeH size={16} />
                  </BotonHerramienta>
                  <BotonHerramienta
                    test="repartir-v"
                    etiqueta={t('editor.distributeV')}
                    onClick={() => distribuirSeleccion('y')}
                  >
                    <IconDistributeV size={16} />
                  </BotonHerramienta>
                </div>
              )}
              {/* AGRUPAR. Un escudo son cinco piezas: sin grupo, moverlo son
                  cinco operaciones y cinco ocasiones de descuadrarlo. */}
              <div className="flex gap-0.5 border-t border-borde pt-1">
                {seleccionadas.length > 1 && (
                  <BotonHerramienta test="agrupar" etiqueta={t('editor.group')} onClick={agruparSeleccion}>
                    <IconGroup size={16} />
                  </BotonHerramienta>
                )}
                {selected && grupoDe(selected) !== null && (
                  <BotonHerramienta test="desagrupar" etiqueta={t('editor.ungroup')} onClick={desagruparSeleccion}>
                    <IconUngroup size={16} />
                  </BotonHerramienta>
                )}
              </div>

              {seleccionadas.length > 1 && (
                <p className="px-1 pb-0.5 text-center text-[10px] leading-tight text-tinta-suave">
                  {t('editor.selectedCount').replace('{n}', String(seleccionadas.length))}
                </p>
              )}
            </div>
          )}

          {/*
            ZOOM.

            Un texto de 4 mm —el minimo del DTF— es ilegible con la zona entera
            en pantalla. Sin acercar no se puede comprobar el detalle que separa
            lo bien impreso de lo mal impreso.
          */}
          {vista === 'plano' && (
            <div
              data-test="barra-zoom"
              className="absolute bottom-3 right-3 z-10 flex items-center gap-0.5 rounded-full border border-borde bg-white/95 p-1 shadow-sm backdrop-blur"
            >
              <BotonHerramienta
                test="alejar"
                etiqueta={t('editor.zoomOut')}
                onClick={() => setEncuadre((v) => acercarEn(v, 1 / 1.25, 0, 0))}
              >
                <IconZoomOut size={16} />
              </BotonHerramienta>
              <span className="min-w-[3rem] text-center text-[11px] tabular-nums text-tinta-suave">
                {Math.round(encuadre.zoom * 100)}%
              </span>
              <BotonHerramienta
                test="acercar"
                etiqueta={t('editor.zoomIn')}
                onClick={() => setEncuadre((v) => acercarEn(v, 1.25, 0, 0))}
              >
                <IconZoomIn size={16} />
              </BotonHerramienta>
              {!esVistaInicial(encuadre) && (
                <BotonHerramienta test="ajustar-vista" etiqueta={t('editor.zoomFit')} onClick={() => setEncuadre(ajustar())}>
                  <IconZoomFit size={16} />
                </BotonHerramienta>
              )}
            </div>
          )}

          {/*
            El lienzo de trabajo NO se desmonta al mirar el mockup: se esconde.
            Desmontarlo tiraría el estado de Fabric y la selección, y volver
            costaría un parpadeo cada vez.
          */}
            {/*
            Qué se ha deshecho. Un cambio que ocurre sin decir cuál deja al
            usuario sin saber si pulsó lo que quería.
          */}
          {ultimoDeshecho && (
            <p
              role="status"
              data-test="aviso-historial"
              className="pointer-events-none absolute left-1/2 top-16 z-10 -translate-x-1/2 rounded-full bg-tinta/85 px-4 py-1.5 text-xs text-white"
            >
              {ultimoDeshecho}
            </p>
          )}

        {/*
          Escondido, pero CON TAMAÑO.

          Estaba con `hidden`, que es `display: none`: el observador de tamaño
          del lienzo pasaba a medir cero, y con cero Fabric se destruye a sí
          mismo. La intención era «no se desmonta, se esconde» —para no perder
          la selección ni pagar un parpadeo al volver— pero con `display: none`
          se desmontaba igual, solo que por dentro.

          Y tenía una consecuencia que no se veía venir: la vista en 3D saca su
          textura de este lienzo, así que al cambiar de vista encontraba un
          lienzo ya muerto y el estampado no aparecía. Pasaba a veces y a veces
          no, según llegara antes el repintado o el observador.

          Sacándolo de la pantalla en vez de apagarlo, conserva su tamaño y
          sigue vivo. `aria-hidden` y `pointer-events-none` lo dejan fuera del
          alcance del teclado y del ratón, que es lo que se pretendía.
        */}
        <div
          className={
            vista === 'plano'
              ? 'h-full w-full'
              : 'pointer-events-none absolute left-[-200vw] top-0 h-full w-full'
          }
          aria-hidden={vista !== 'plano'}
        >
            <FabricCanvas
              document={document}
              zonePolygonMm={context.zone.polygonMm}
              assetUrls={assetUrls}
              garmentRgb={context.darkGarment ? { r: 34, g: 34, b: 34 } : { r: 244, g: 243, b: 240 }}
              selectedLayerId={selectedLayerId}
              layersWithErrors={errorLayers}
              vista={encuadre}
              onVista={setEncuadre}
              etiqueta={t('editor.canvasLabel')}
              onExportador={(exportar) => {
                exportarDisenoRef.current = exportar;
              }}
            />
          </div>

          {vista === 'tres-d' && (
            <div className="h-full overflow-y-auto p-4 pt-16">
              {/*
                Ya no es un cilindro del color de la prenda con el diseño pegado
                delante —«el 3D no se ve»—: es un volteo entre las FOTOS reales
                de cada cara, con su color, sus pliegues y el estampado a la
                escala de la talla. Gira fotos de verdad, no finge geometría.
              */}
              <CarruselPrendas
                caraActual={{
                  clave: designId,
                  titulo: context.zone.name,
                  document,
                  zonePolygonMm: context.zone.polygonMm,
                  zoneOffsetMm: context.mockup.zoneOffsetMm,
                  encaje: context.mockup,
                  assetUrls,
                  dark: context.darkGarment,
                  caraPedida: context.viewCode,
                  caraMostrada: context.mockup.caraMostrada,
                  techniqueId: context.technique.techniqueId,
                }}
                otras={grupo ? grupo.zonas.filter((z) => z.designId !== designId) : []}
                colorId={context.colorId}
                catalogo={catalogoCaras ?? { views: [], colores: [], zones: [] }}
                factorTalla={escalaTallaMockup?.factor ?? 1}
                alto="h-[26rem]"
              />
            </div>
          )}

                    {vista === 'puesto' && context.puesto && (
            <div className="h-full overflow-y-auto p-4 pt-16">
              <div className="mx-auto max-w-md">
                {/* NO editable: es la foto de un modelo, no una mesa de trabajo.
                    El diseño se coloca con el mismo mecanismo ancla+escala del
                    mockup, deformado por los pliegues y escalado a la talla. */}
                <MockupPrenda
                  document={document}
                  zonePolygonMm={context.zone.polygonMm}
                  zoneOffsetMm={context.mockup.zoneOffsetMm}
                  encaje={
                    encajePuestoLocal
                      ? {
                          ...context.puesto,
                          anchor: { xFrac: encajePuestoLocal.xFrac, yFrac: encajePuestoLocal.yFrac },
                          mmPerPixel: encajePuestoLocal.mmPerPixel,
                        }
                      : context.puesto
                  }
                  assetUrls={assetUrls}
                  dark={context.darkGarment}
                  factorTalla={escalaTallaMockup?.factor ?? 1}
                  ajustable={ajustandoPuesto}
                  onEncaje={setEncajePuestoLocal}
                  techniqueId={context.technique.techniqueId}
                />

                {/*
                  EL COLOR DEL MODELO ES DE MUESTRA. La foto «puesto» la trae el
                  proveedor en UN color; no se puede reteñir con fidelidad, así
                  que en vez de fingir otro color se dice la verdad y se manda a
                  la vista sobre prenda, que sí es del color elegido.
                */}
                {context.colores.length > 1 && (
                  <p
                    data-test="puesto-color-muestra"
                    className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900"
                  >
                    El modelo lleva un color de muestra. Para ver «{context.colorName}» exacto, usa la vista sobre
                    prenda.
                  </p>
                )}

                {/*
                  AJUSTAR EL ENCAJE DEL PUESTO, solo administración. La medición
                  automática de una foto de modelo es la más floja (0,35); esto
                  la corrige arrastrando y la guarda contra la vista «puesto».
                */}
                {esAdmin && (
                  <div className="mt-3 rounded-lg border border-borde p-2">
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        data-test="ajustar-puesto"
                        checked={ajustandoPuesto}
                        onChange={(e) => setAjustandoPuesto(e.target.checked)}
                      />
                      {t('editor.adjustFit')}
                    </label>
                    {ajustandoPuesto && (
                      <div className="mt-2 space-y-2">
                        <p className="text-[11px] text-tinta-suave">{t('editor.adjustFitHelp')}</p>
                        <Field label={t('editor.mmPerPixel')}>
                          <NumberInput
                            value={encajePuestoLocal?.mmPerPixel ?? context.puesto.mmPerPixel}
                            step={0.01}
                            min={0.1}
                            max={20}
                            onChange={(mmPerPixel) =>
                              setEncajePuestoLocal({
                                xFrac: encajePuestoLocal?.xFrac ?? context.puesto?.anchor?.xFrac ?? 0.5,
                                yFrac: encajePuestoLocal?.yFrac ?? context.puesto?.anchor?.yFrac ?? 0.25,
                                mmPerPixel,
                              })
                            }
                          />
                        </Field>
                        <Button
                          data-test="guardar-encaje-puesto"
                          className="w-full"
                          onClick={() => void guardarEncajePuesto()}
                          disabled={!encajePuestoLocal}
                        >
                          {t('editor.saveFit')}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/*
                  PROBAR LA TALLA: el mismo diseño, a la escala de cada talla.
                  Una 4 sale más pequeña que una XL, y eso es justo lo que hay
                  que ver antes de encargar para un equipo con tallas mezcladas.
                */}
                {context.sizes.length > 1 && (
                  <div className="mt-3">
                    <span className="mb-1 block text-xs text-tinta-suave">Ver en la talla</span>
                    <ul className="flex flex-wrap gap-1.5">
                      {context.sizes.map((s) => {
                        const activa = (tallaMockup ?? context.size) === s;
                        return (
                          <li key={s}>
                            <button
                              type="button"
                              data-test="talla-mockup"
                              aria-pressed={activa}
                              onClick={() => void probarTallaMockup(s)}
                              className={`toque rounded-md border px-2.5 py-1 text-xs ${
                                activa ? 'border-marca bg-marca text-white' : 'border-borde text-tinta-suave'
                              }`}
                            >
                              {s}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {vista === 'prenda' && (
            <div className="h-full overflow-y-auto p-4 pt-16">
              <div className="mx-auto max-w-md">
                <MockupPrenda
                  document={document}
                  zonePolygonMm={context.zone.polygonMm}
                  zoneOffsetMm={context.mockup.zoneOffsetMm}
                  encaje={
                    encajeLocal
                      ? {
                          ...context.mockup,
                          anchor: { xFrac: encajeLocal.xFrac, yFrac: encajeLocal.yFrac },
                          mmPerPixel: encajeLocal.mmPerPixel,
                        }
                      : context.mockup
                  }
                  assetUrls={assetUrls}
                  dark={context.darkGarment}
                  editable
                  selectedLayerId={selectedLayerId}
                  layersWithErrors={errorLayers}
                  ajustable={ajustandoEncaje}
                  onEncaje={setEncajeLocal}
                  caraPedida={context.viewCode}
                  caraMostrada={context.mockup.caraMostrada}
                />
                {/*
                  AJUSTAR EL ENCAJE, solo para quien administra el catálogo.

                  El encaje se hereda de la familia con un 50 % de confianza y
                  hasta hoy solo se podía corregir escribiendo fracciones. Con
                  470 productos, eso no lo hace nadie. Arrastrando el recuadro
                  sobre la foto se ajusta en segundos, y la confianza pasa a 1
                  porque alguien lo ha MIRADO en esta prenda.
                */}
                {esAdmin && (
                  <div className="mt-3 rounded-lg border border-borde p-2">
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        data-test="ajustar-encaje"
                        checked={ajustandoEncaje}
                        onChange={(e) => setAjustandoEncaje(e.target.checked)}
                      />
                      {t('editor.adjustFit')}
                    </label>
                    {ajustandoEncaje && (
                      <div className="mt-2 space-y-2">
                        <p className="text-[11px] text-tinta-suave">{t('editor.adjustFitHelp')}</p>
                        <Field label={t('editor.mmPerPixel')}>
                          <NumberInput
                            value={context.mockup.mmPerPixel}
                            step={0.01}
                            min={0.1}
                            max={20}
                            onChange={(mmPerPixel) =>
                              encajeLocal
                                ? setEncajeLocal({ ...encajeLocal, mmPerPixel })
                                : setEncajeLocal({
                                    xFrac: context.mockup.anchor?.xFrac ?? 0.5,
                                    yFrac: context.mockup.anchor?.yFrac ?? 0.25,
                                    mmPerPixel,
                                  })
                            }
                          />
                        </Field>
                        <Button
                          data-test="guardar-encaje"
                          className="w-full"
                          onClick={() => void guardarEncaje()}
                          disabled={!encajeLocal}
                        >
                          {t('editor.saveFit')}
                        </Button>
                        {/* La zona viene del patrón con una orientación; girarla
                            un cuarto de vuelta es lo que hace falta cuando el
                            arte va apaisado. */}
                        <Button
                          variant="secondary"
                          data-test="girar-zona"
                          className="w-full"
                          onClick={() => void girarZona()}
                        >
                          {t('editor.rotateZone', {
                            ancho: formatMm(zoneBounds.heightMm),
                            alto: formatMm(zoneBounds.widthMm),
                          })}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Button variant="secondary" data-test="descargar-mockup" onClick={() => void descargarVistaPrenda()}>
                    {t('editor.downloadMockup')}
                  </Button>
                  <Button
                    variant="secondary"
                    data-test="hoja-prueba"
                    disabled={descargandoHoja || document.layers.length === 0}
                    onClick={() => void descargarHojaPrueba()}
                  >
                    {t('editor.proofSheet')}
                  </Button>
                  {/* Casi ningún pedido de grupo lo decide una sola persona. */}
                  <Button variant="secondary" data-test="compartir" onClick={() => void compartir()}>
                    {t('editor.share')}
                  </Button>
                </div>
                {enlaceCompartido && (
                  <div className="mt-2 rounded-lg border border-borde p-2">
                    <p className="text-xs text-tinta-suave">{t('editor.shareReady')}</p>
                    {/* Solo lectura: el enlace se copia, no se escribe encima. */}
                    <input
                      readOnly
                      data-test="enlace-compartido"
                      value={enlaceCompartido}
                      onFocus={(e) => e.currentTarget.select()}
                      className="mt-1 w-full rounded border border-borde p-1.5 text-xs"
                    />
                  </div>
                )}

                {/*
                  PECHO Y ESPALDA A LA VEZ: mientras se trabaja una cara, las
                  otras del grupo se ven aquí abajo, en lectura, cada una sobre
                  su propia foto. Es lo que convierte «un diseño por zona» en
                  «una prenda» a ojos de quien la encarga.
                */}
                {grupo && catalogoCaras && (
                  <OtrasCaras
                    zonas={grupo.zonas}
                    designIdActual={designId}
                    colorId={context.colorId}
                    catalogo={catalogoCaras}
                    factorTalla={escalaTallaMockup?.factor ?? 1}
                  />
                )}
              </div>
            </div>
          )}

          {/*
            LIENZO VACÍO = TRES ACCIONES, no una frase.

            Los tres únicos caminos reales de arranque son estos; ponerlos en el
            centro ahorra descubrir la barra de herramientas, que en móvil no es
            obvia. `pointer-events` solo en los botones: el lienzo de debajo
            sigue recibiendo los gestos.
          */}
          {document.layers.length === 0 && vista === 'plano' && (
            <div className="pointer-events-none absolute inset-x-0 bottom-24 flex flex-col items-center gap-2">
              <p className="text-sm text-tinta-suave">{t('editor.emptyDesign')}</p>
              <div className="flex flex-wrap justify-center gap-2">
                <button
                  data-test="vacio-texto"
                  onClick={addText}
                  className="toque pointer-events-auto rounded-full bg-marca px-4 py-2 text-sm font-medium text-white"
                >
                  {t('editor.addText')}
                </button>
                <button
                  data-test="vacio-imagen"
                  onClick={() => fileRef.current?.click()}
                  className="toque pointer-events-auto rounded-full border border-borde bg-white px-4 py-2 text-sm"
                >
                  {t('editor.addImage')}
                </button>
                <button
                  data-test="vacio-plantillas"
                  onClick={() => setSheet('plantillas')}
                  className="toque pointer-events-auto rounded-full border border-borde bg-white px-4 py-2 text-sm"
                >
                  {t('editor.templates')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* --- PC: propiedades y diagnosticos a la derecha -------------------- */}
        <aside className="hidden w-80 shrink-0 space-y-3 overflow-y-auto border-l border-borde p-3 lg:block">
          <PropertiesPanel layer={selected} onChange={updateLayer} onCenter={centerLayer} />
          <PanelTintas
            layers={document.layers}
            prendaOscura={context.darkGarment}
            tecnica={context.technique.techniqueId}
            maximoTintas={context.technique.maxInks ?? 99}
          />
          <AvisoContraste layers={document.layers} prenda={colorDePrenda} onFix={updateLayer} />
          <DiagnosticsPanel
            diagnostics={validation.diagnostics}
            layers={document.layers}
            zonePolygonMm={context.zone.polygonMm}
            onFix={updateLayer}
          />
        </aside>
      </div>

      {/* --- MOVIL: barra inferior al alcance del pulgar ---------------------- */}
      <nav className="safe-bottom flex items-stretch gap-1 border-t border-borde bg-white px-2 py-1 lg:hidden">
        <ToolButton
          test="anadir-imagen"
          icon={<IconImage />}
          label={t('editor.addImage')}
          onClick={() => fileRef.current?.click()}
        />
        <ToolButton test="anadir-texto" icon={<IconText />} label={t('editor.addText')} onClick={addText} />
        <ToolButton test="anadir-forma" icon={<IconShape />} label={t('editor.addShape')} onClick={addShape} />
        <ToolButton icon={<IconLayers />} label={t('editor.layers')} onClick={() => setSheet('layers')} />
        <ToolButton
          test="revision-tecnica"
          icon={
            validation.counts.error > 0 ? (
              <span className="relative">
                <IconCheck />
                <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-error" />
              </span>
            ) : (
              <IconCheck />
            )
          }
          label={t('diagnostics.title')}
          onClick={() => setSheet('diagnostics')}
        />
      </nav>

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void onUpload(file);
          e.target.value = '';
        }}
      />

      {/*
        FORMAS. Un escudo y una banderola resuelven la estructura de la mayoría
        de los diseños de peña y de equipo sin subir nada.
      */}
      <Sheet enEscritorio open={sheet === 'formas'} onClose={() => setSheet(null)} title={t('editor.addShape')}>
        <ul className="grid grid-cols-3 gap-2">
          {FORMAS_DISPONIBLES.map((forma) => (
            <li key={forma}>
              <button
                type="button"
                data-test={`forma-${forma}`}
                onClick={() => {
                  addLayer(createShapeLayer({ shape: forma, color: tintaPorDefecto, zone: zoneBounds }));
                  setSheet(null);
                }}
                className="toque flex w-full flex-col items-center gap-1 rounded-lg border border-borde p-2"
              >
                {/* La miniatura se dibuja con el MISMO generador que la capa: si
                    algún día cambia la forma, el icono cambia con ella. */}
                <svg viewBox="0 0 40 40" className="h-10 w-10" aria-hidden="true">
                  <path
                    d={trazadoDeForma(forma, 36, 36 / proporcionDeForma(forma))}
                    transform="translate(2, 2)"
                    fill={esSoloTrazo(forma) ? 'none' : 'currentColor'}
                    stroke="currentColor"
                    strokeWidth={esSoloTrazo(forma) ? 2 : 0}
                  />
                </svg>
                <span className="text-[10px] leading-tight text-tinta-suave">{t(`shape.${forma}`)}</span>
              </button>
            </li>
          ))}
        </ul>
      </Sheet>

      {/*
        PLANTILLAS. El lienzo vacío es donde más gente abandona: «no sé qué
        poner» no lo arregla ninguna herramienta, pero «cambio el nombre y ya» sí.
      */}
      <Sheet enEscritorio open={sheet === 'equipo'} onClose={() => setSheet(null)} title={t('equipo.titulo')}>
        <PanelEquipo
          designId={designId}
          document={document}
          zone={context.zone}
          technique={context.technique}
          substrate={context.substrate}
          sizes={context.sizes}
          onError={setError}
        />
      </Sheet>

      <Sheet enEscritorio open={sheet === 'plantillas'} onClose={() => setSheet(null)} title={t('editor.templates')}>
        <p className="mb-3 text-xs text-tinta-suave">{t('editor.templatesHelp')}</p>
        <ul className="space-y-2">
          {PLANTILLAS.map((plantilla) => (
            <li key={plantilla.id}>
              <button
                type="button"
                data-test={`plantilla-${plantilla.id}`}
                onClick={() => {
                  /*
                    Con campos, primero el formulario «rellena y listo»: el
                    cliente escribe su nombre y su dorsal UNA vez y las capas
                    salen ya con lo suyo, en vez de insertar el ejemplo y andar
                    sustituyendo textos capa a capa.
                  */
                  if (plantilla.campos?.length) {
                    setPlantillaConCampos(plantilla.id);
                    setValoresPlantilla(Object.fromEntries(plantilla.campos.map((c) => [c.id, ''])));
                    return;
                  }
                  insertarPlantilla(plantilla.id, tintaPorDefecto);
                  setSheet(null);
                }}
                className="toque flex w-full items-center justify-between rounded-lg border border-borde px-3 py-2 text-left"
              >
                <span className="text-sm">{t(plantilla.clave)}</span>
                <Badge>{t(`templateCat.${plantilla.categoria}`)}</Badge>
              </button>

              {plantillaConCampos === plantilla.id && (
                <div data-test="plantilla-campos" className="mt-2 space-y-2 rounded-lg border border-borde bg-papel-suave p-3">
                  {(plantilla.campos ?? []).map((campo) => (
                    <Field key={campo.id} label={t(campo.clave)}>
                      <input
                        data-test={`campo-${campo.id}`}
                        value={valoresPlantilla[campo.id] ?? ''}
                        placeholder={campo.porDefecto}
                        onChange={(e) =>
                          setValoresPlantilla((v) => ({ ...v, [campo.id]: e.target.value }))
                        }
                        className="w-full rounded-lg border border-borde px-2 py-2 text-sm"
                      />
                    </Field>
                  ))}
                  <button
                    data-test="plantilla-insertar"
                    onClick={() => {
                      insertarPlantilla(plantilla.id, tintaPorDefecto, valoresPlantilla);
                      setPlantillaConCampos(null);
                      setSheet(null);
                    }}
                    className="toque w-full rounded-lg bg-marca px-3 py-2 text-sm font-medium text-white"
                  >
                    {t('editor.templateInsert')}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </Sheet>

      <Sheet enEscritorio open={sheet === 'biblioteca'} onClose={() => setSheet(null)} title={t('editor.myImages')}>
        <BibliotecaImagenes
          designId={designId}
          onElegir={(asset) => {
            colocarImagen(asset);
            setSheet(null);
          }}
        />
      </Sheet>

      <Sheet open={sheet === 'layers'} onClose={() => setSheet(null)} title={t('editor.layers')}>
        <LayersPanel
          layers={document.layers}
          selectedId={selectedLayerId}
          onSelect={(id) => {
            seleccionarConGrupo(id);
            setSheet('properties');
          }}
          onMove={moveLayer}
          onRemove={removeLayer}
          onToggleLock={(layer) => updateLayer(layer.id, { locked: !layer.locked } as Partial<Layer>)}
        />
      </Sheet>

      <Sheet open={sheet === 'properties'} onClose={() => setSheet(null)} title={t('editor.properties')}>
        {/* En el móvil, el ajuste fino va ARRIBA del todo: es lo que más se usa
            y lo que peor se hace con el dedo. */}
        {selected && (
          <div className="mb-3">
            <MandoFino onMover={(dx, dy) => desplazarSeleccion(dx, dy)} />
          </div>
        )}
        <PropertiesPanel layer={selected} onChange={updateLayer} onCenter={centerLayer} />
      </Sheet>

      <Sheet open={sheet === 'diagnostics'} onClose={() => setSheet(null)} title={t('diagnostics.title')}>
        <div className="space-y-3">
          <PanelTintas
            layers={document.layers}
            prendaOscura={context.darkGarment}
            tecnica={context.technique.techniqueId}
            maximoTintas={context.technique.maxInks ?? 99}
          />
          <AvisoContraste layers={document.layers} prenda={colorDePrenda} onFix={updateLayer} />
          <DiagnosticsPanel
            diagnostics={validation.diagnostics}
            layers={document.layers}
            zonePolygonMm={context.zone.polygonMm}
            onFix={updateLayer}
          />
        </div>
      </Sheet>

      {lastError && saveState === 'error' && (
        <div className="p-3">
          <Banner tone="error" title={t('editor.saveError')}>{lastError}</Banner>
        </div>
      )}
    </div>
  );
}

function ToolButton({
  icon,
  label,
  onClick,
  test,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  test?: string;
}) {
  return (
    <button
      data-test={test}
      onClick={onClick}
      title={label}
      className="toque flex flex-1 flex-col items-center gap-0.5 rounded-lg py-1.5 text-[11px] hover:bg-papel-suave"
    >
      {icon}
      <span className="truncate">{label}</span>
    </button>
  );
}

function SaveIndicator({ state, onRetry }: { state: string; onRetry: () => void }) {
  const t = useT();
  if (state === 'saving' || state === 'pending') {
    return (
      <span className="flex items-center gap-1 text-xs text-tinta-suave">
        <IconSpinner size={14} /> {t('editor.saving')}
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span className="flex items-center gap-1 text-xs text-ok">
        <IconCheck size={14} /> {t('editor.saved')}
      </span>
    );
  }
  if (state === 'error') {
    return (
      <button onClick={onRetry} className="text-xs text-error underline">
        {t('app.retry')}
      </button>
    );
  }
  return null;
}

function LayersPanel({
  layers,
  selectedId,
  onSelect,
  onMove,
  onRemove,
  onToggleLock,
}: {
  layers: Layer[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onMove: (id: string, direction: 'up' | 'down') => void;
  onRemove: (id: string) => void;
  onToggleLock: (layer: Layer) => void;
}) {
  const t = useT();
  const ordered = [...layers].sort((a, b) => b.z - a.z);

  if (ordered.length === 0) {
    return <p className="p-3 text-sm text-tinta-suave">{t('editor.emptyDesign')}</p>;
  }

  return (
    <ul className="space-y-1">
      {ordered.map((layer) => (
        <li
          key={layer.id}
          data-test="capa"
          className={`flex items-center gap-1 rounded-lg border p-2 ${
            layer.id === selectedId ? 'border-marca bg-red-50' : 'border-transparent hover:bg-papel-suave'
          }`}
        >
          <button onClick={() => onSelect(layer.id)} className="min-w-0 flex-1 text-left">
            <span className="flex items-center gap-2">
              {layer.kind === 'image' ? <IconImage size={16} /> : layer.kind === 'text' ? <IconText size={16} /> : <IconShape size={16} />}
              <span className="truncate text-sm">
                {layer.name ?? (layer.kind === 'text' ? layer.text.slice(0, 24) : `${t('editor.layer')} ${layer.z + 1}`)}
              </span>
            </span>
          </button>
          <button onClick={() => onMove(layer.id, 'up')} className="toque rounded p-1" aria-label={t('editor.bringForward')}>
            <IconUp size={16} />
          </button>
          <button onClick={() => onMove(layer.id, 'down')} className="toque rounded p-1" aria-label={t('editor.sendBackward')}>
            <IconDown size={16} />
          </button>
          <button onClick={() => onToggleLock(layer)} className="toque rounded p-1" aria-label={t('editor.lock')}>
            {layer.locked ? <IconLock size={16} /> : <IconUnlock size={16} />}
          </button>
          <button onClick={() => onRemove(layer.id)} className="toque rounded p-1 text-error" aria-label={t('editor.delete')}>
            <IconTrash size={16} />
          </button>
        </li>
      ))}
    </ul>
  );
}


/**
 * Boton de una barra flotante: cuadrado, tactil y con su nombre en el titulo.
 *
 * El significado va SIEMPRE en `aria-label`, no en el icono: un trazo sin texto
 * no lo lee un lector de pantalla y no lo entiende quien no conoce el simbolo.
 */
/**
 * LAS IMÁGENES QUE ESTE CLIENTE YA SUBIÓ.
 *
 * Estaban guardadas desde el primer día —el almacén las deduplica por
 * checksum— pero no había forma de volver a ellas: había que buscar otra vez el
 * fichero en el móvil. Con 90 días de retención, casi siempre están ahí.
 *
 * Se cargan al abrir la hoja y no antes: son URL firmadas que caducan, y
 * pedirlas al montar el editor sería gastarlas sin que nadie las mire.
 */
function BibliotecaImagenes({
  designId,
  onElegir,
}: {
  designId: string;
  onElegir: (asset: { checksum: string; widthPx: number; heightPx: number; previewUrl: string }) => void;
}) {
  const t = useT();
  const [items, setItems] = useState<Array<{
    checksum: string;
    widthPx: number;
    heightPx: number;
    previewUrl: string;
  }> | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api
      .misImagenes(designId)
      .then((r) => {
        if (vivo) setItems(r.items);
      })
      .catch((err: Error) => {
        if (vivo) setFallo(err.message);
      });
    return () => {
      vivo = false;
    };
  }, [designId]);

  if (fallo) return <Banner tone="error" title={t('app.error')}>{fallo}</Banner>;
  if (!items) return <Spinner label={t('app.loading')} />;
  if (items.length === 0) return <p className="p-4 text-sm text-tinta-suave">{t('editor.noImagesYet')}</p>;

  return (
    <div className="space-y-3">
      <p className="text-xs text-tinta-suave">{t('editor.reuseImage')}</p>
      <ul className="grid grid-cols-3 gap-2">
        {items.map((asset) => (
          <li key={asset.checksum}>
            <button
              type="button"
              data-test="imagen-biblioteca"
              onClick={() => onElegir(asset)}
              className="toque block w-full overflow-hidden rounded-lg border border-borde bg-fondo-suave"
            >
              {/* El texto alternativo describe la FUNCIÓN, no el contenido: no
                  sabemos qué hay en la imagen del cliente y no vamos a inventarlo. */}
              <img src={asset.previewUrl} alt={t('editor.reuseThis')} className="block h-20 w-full object-contain" />
              <span className="block px-1 py-0.5 text-[10px] tabular-nums text-tinta-suave">
                {asset.widthPx} × {asset.heightPx}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * MANDO DE AJUSTE FINO.
 *
 * Arrastrar con el dedo tiene una precisión de varios milímetros. En una zona
 * de 280 mm eso es la diferencia entre centrado y torcido, y además la mano
 * tapa justo lo que se está mirando.
 *
 * Cuatro flechas y un paso: 1 mm normal, 10 mm con el botón de paso grueso.
 * Es lo mismo que hacen las flechas del teclado, que aquí no existen.
 */
function MandoFino({ onMover }: { onMover: (dx: number, dy: number) => void }) {
  const t = useT();
  const [pasoGrueso, setPasoGrueso] = useState(false);
  const paso = pasoGrueso ? 10 : 1;

  const flecha = (dx: number, dy: number, etiqueta: string, icono: React.ReactNode, test: string) => (
    <button
      type="button"
      data-test={test}
      aria-label={etiqueta}
      onClick={() => onMover(dx * paso, dy * paso)}
      className="toque flex h-10 w-10 items-center justify-center rounded-lg border border-borde"
    >
      {icono}
    </button>
  );

  return (
    <div className="flex items-center gap-2" data-test="mando-fino">
      <div className="grid grid-cols-3 gap-1">
        <span />
        {flecha(0, -1, t('editor.nudgeUp'), <IconUp size={16} />, 'fino-arriba')}
        <span />
        {flecha(-1, 0, t('editor.nudgeLeft'), <IconBack size={16} />, 'fino-izquierda')}
        <button
          type="button"
          data-test="fino-paso"
          aria-pressed={pasoGrueso}
          onClick={() => setPasoGrueso((v) => !v)}
          className={`toque flex h-10 w-10 items-center justify-center rounded-lg border text-[11px] tabular-nums ${
            pasoGrueso ? 'border-marca bg-marca text-white' : 'border-borde'
          }`}
        >
          {paso}
        </button>
        {flecha(1, 0, t('editor.nudgeRight'), <IconBack size={16} className="rotate-180" />, 'fino-derecha')}
        <span />
        {flecha(0, 1, t('editor.nudgeDown'), <IconDown size={16} />, 'fino-abajo')}
        <span />
      </div>
      <p className="text-[11px] leading-tight text-tinta-suave">{t('editor.nudgeHelp')}</p>
    </div>
  );
}

function BotonHerramienta({
  children,
  etiqueta,
  onClick,
  test,
}: {
  children: React.ReactNode;
  etiqueta: string;
  onClick: () => void;
  test?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={etiqueta}
      title={etiqueta}
      {...(test ? { 'data-test': test } : {})}
      className="toque flex h-8 w-8 items-center justify-center rounded-lg text-tinta-suave hover:bg-fondo-suave hover:text-tinta"
    >
      {children}
    </button>
  );
}

/**
 * LO QUE SE PUEDE HACER CON UNA IMAGEN.
 *
 * Hasta aquí se subía y se colocaba, y nada más. Estas cuatro cosas son las que
 * convierten «el fichero que manda el cliente» en algo imprimible:
 *
 *  - **Hasta qué tamaño llega.** El aviso de resolución dice «39 ppp, exige
 *    150»: correcto, pero el cliente no sabe qué hacer con eso. La medida
 *    concreta sí, y además se puede ofrecer el botón que la aplica.
 *  - **Quitar el fondo.** Un logotipo con fondo blanco sobre camiseta negra
 *    queda como una pegatina. Es el problema número uno de lo que llega.
 *  - **Vectorizar.** Serigrafía y vinilo NECESITAN vectores; sin esto, quien
 *    llega con un JPEG pequeño se queda sin salida.
 *  - **Recortar y ajustar.** La foto del móvil viene con medio salón alrededor.
 *
 * Las dos operaciones que tocan píxeles se hacen en el SERVIDOR y devuelven un
 * asset nuevo: el original se conserva, y el checksum del diseño sigue
 * apuntando a un fichero que existe y no ha cambiado.
 */
function PanelImagen({
  layer,
  onChange,
}: {
  layer: Extract<Layer, { kind: 'image' }>;
  onChange: (id: string, patch: Partial<Layer>) => void;
}) {
  const t = useT();
  const context = useEditor((s) => s.context);
  const redimensionar = useEditor((s) => s.redimensionar);
  const [trabajando, setTrabajando] = useState<'quitar-fondo' | 'vectorizar' | null>(null);
  const [avisoTratamiento, setAvisoTratamiento] = useState<string | null>(null);

  const dpiMinimo = context?.technique.minEffectiveDpi ?? 150;
  const pixeles = pixelesTrasRecorte(layer.intrinsic, layer.recorte);
  const maxima = medidaMaximaMm(pixeles, dpiMinimo);
  const anchoActual = Math.hypot(layer.transform.a, layer.transform.b);
  const seExcede = anchoActual > maxima.widthMm + 0.5;

  const ajustes = layer.ajustes ?? AJUSTES_NEUTROS;
  const cambiarAjuste = (patch: Partial<typeof ajustes>) =>
    onChange(layer.id, { ajustes: { ...ajustes, ...patch } } as Partial<Layer>);

  const tratar = async (operacion: 'quitar-fondo' | 'vectorizar') => {
    setTrabajando(operacion);
    setAvisoTratamiento(null);
    try {
      const r = await api.tratarAsset(layer.assetChecksum, { operacion });
      /*
        Se cambia el checksum de la capa y punto: el tamaño colocado NO se toca.
        Es la misma imagen tratada, así que moverla o reescalarla porque haya
        pasado por el servidor sería una sorpresa que nadie ha pedido.
      */
      onChange(layer.id, {
        assetChecksum: r.checksum,
        intrinsic: { ...layer.intrinsic, widthPx: r.widthPx, heightPx: r.heightPx },
      } as Partial<Layer>);
    } catch (err) {
      setAvisoTratamiento((err as Error).message);
    } finally {
      setTrabajando(null);
    }
  };

  return (
    <div className="space-y-3 border-t border-borde pt-3">
      {/* HASTA QUÉ TAMAÑO llega esta imagen, y el botón que la ajusta. */}
      <div className="rounded-lg bg-fondo-suave p-2 text-xs">
        <p className="text-tinta-suave">
          {t('editor.maxSize')
            .replace('{w}', formatMm(maxima.widthMm))
            .replace('{h}', formatMm(maxima.heightMm))
            .replace('{dpi}', String(dpiMinimo))}
        </p>
        {seExcede && (
          <button
            type="button"
            data-test="ajustar-al-maximo"
            onClick={() => redimensionar(layer.id, maxima.widthMm, null)}
            className="toque mt-1.5 w-full rounded-lg border border-marca px-2 py-1.5 text-marca"
          >
            {t('editor.fitToMax').replace('{w}', formatMm(maxima.widthMm))}
          </button>
        )}
      </div>

      {/* QUITAR EL FONDO y VECTORIZAR. */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          data-test="quitar-fondo"
          disabled={trabajando !== null}
          onClick={() => void tratar('quitar-fondo')}
          className="toque flex items-center justify-center gap-1.5 rounded-lg border border-borde px-2 py-2 text-xs disabled:opacity-50"
        >
          {trabajando === 'quitar-fondo' ? <IconSpinner size={14} /> : <IconEraser size={14} />}
          {t('editor.removeBackground')}
        </button>
        <button
          type="button"
          data-test="vectorizar"
          disabled={trabajando !== null}
          onClick={() => void tratar('vectorizar')}
          className="toque flex items-center justify-center gap-1.5 rounded-lg border border-borde px-2 py-2 text-xs disabled:opacity-50"
        >
          {trabajando === 'vectorizar' ? <IconSpinner size={14} /> : <IconVector size={14} />}
          {t('editor.vectorize')}
        </button>
      </div>

      {avisoTratamiento && (
        <p role="status" className="rounded-lg bg-aviso/10 p-2 text-xs text-aviso">
          {avisoTratamiento}
        </p>
      )}

      {/* RECORTE con proporción fija: «cuadrado» se entiende, «0,5625» no. */}
      <Field label={t('editor.crop')}>
        <div className="flex flex-wrap gap-1.5">
          {(
            [
              { clave: 'libre', proporcion: 0 },
              { clave: 'cuadrado', proporcion: 1 },
              { clave: 'apaisado', proporcion: 4 / 3 },
              { clave: 'vertical', proporcion: 3 / 4 },
            ] as const
          ).map(({ clave, proporcion }) => (
            <button
              key={clave}
              type="button"
              data-test={`recorte-${clave}`}
              onClick={() =>
                onChange(layer.id, {
                  recorte:
                    proporcion === 0 ? undefined : recorteConProporcion(layer.intrinsic, proporcion),
                } as Partial<Layer>)
              }
              className="toque rounded-lg border border-borde px-2.5 py-1.5 text-xs"
            >
              {t(`editor.crop_${clave}`)}
            </button>
          ))}
        </div>
      </Field>

      {/* AJUSTES. Pasar una foto a una tinta es lo que la hace imprimible en
          serigrafía barata, y el alto contraste salva los logotipos escaneados. */}
      <Field label={t('editor.imageMode')}>
        <div className="flex flex-wrap gap-1.5">
          {(
            [
              // La clave del texto va aquí y no calculada: 'contraste-alto' no es un
              // identificador válido, y componer la clave a mano se rompe en silencio.
              { modo: 'ninguno', clave: 'mode_ninguno' },
              { modo: 'grises', clave: 'mode_grises' },
              { modo: 'contraste-alto', clave: 'mode_contrasteAlto' },
              { modo: 'una-tinta', clave: 'mode_unaTinta' },
            ] as const
          ).map(({ modo, clave }) => (
            <button
              key={modo}
              type="button"
              data-test={`modo-${modo}`}
              aria-pressed={ajustes.modo === modo}
              onClick={() => cambiarAjuste({ modo })}
              className={`toque rounded-lg border px-2.5 py-1.5 text-xs ${
                ajustes.modo === modo ? 'border-marca bg-marca text-white' : 'border-borde'
              }`}
            >
              {t(`editor.${clave}`)}
            </button>
          ))}
        </div>
      </Field>

      <div className="grid grid-cols-2 gap-2">
        <Field label={t('editor.brightness')}>
          <NumberInput
            value={ajustes.brillo}
            step={0.05}
            min={0}
            max={3}
            onChange={(brillo) => cambiarAjuste({ brillo })}
          />
        </Field>
        <Field label={t('editor.contrast')}>
          <NumberInput
            value={ajustes.contraste}
            step={0.05}
            min={0}
            max={3}
            onChange={(contraste) => cambiarAjuste({ contraste })}
          />
        </Field>
        {/* La saturación no pinta nada en los modos que ya pasan a gris. */}
        {ajustes.modo === 'ninguno' && (
          <Field label={t('editor.saturation')}>
            <NumberInput
              value={ajustes.saturacion}
              step={0.05}
              min={0}
              max={3}
              onChange={(saturacion) => cambiarAjuste({ saturacion })}
            />
          </Field>
        )}
        {necesitaUmbral(ajustes) && (
          <Field label={t('editor.threshold')}>
            <NumberInput
              value={ajustes.umbral}
              step={0.05}
              min={0}
              max={1}
              onChange={(umbral) => cambiarAjuste({ umbral })}
            />
          </Field>
        )}
      </div>
    </div>
  );
}

function PropertiesPanel({
  layer,
  onChange,
  onCenter,
}: {
  layer: Layer | null;
  onChange: (id: string, patch: Partial<Layer>) => void;
  onCenter: (id: string) => void;
}) {
  const t = useT();
  const proporcionBloqueada = useEditor((s) => s.proporcionBloqueada);
  const cambiarProporcionBloqueada = useEditor((s) => s.cambiarProporcionBloqueada);
  const redimensionar = useEditor((s) => s.redimensionar);
  if (!layer) return <Card><p className="text-sm text-tinta-suave">{t('editor.properties')}</p></Card>;

  const widthMm = Math.hypot(layer.transform.a, layer.transform.b);
  const heightMm = Math.hypot(layer.transform.c, layer.transform.d);
  const rotation = (Math.atan2(layer.transform.b, layer.transform.a) * 180) / Math.PI;

  const setTransform = (patch: Partial<{ x: number; y: number; w: number; h: number; deg: number }>) => {
    const w = patch.w ?? widthMm;
    const h = patch.h ?? heightMm;
    const deg = patch.deg ?? rotation;
    const rad = (deg * Math.PI) / 180;
    onChange(layer.id, {
      transform: {
        a: Math.cos(rad) * w,
        b: Math.sin(rad) * w,
        c: -Math.sin(rad) * h,
        d: Math.cos(rad) * h,
        e: patch.x ?? layer.transform.e,
        f: patch.y ?? layer.transform.f,
      },
    } as Partial<Layer>);
  };

  return (
    <Card className="space-y-3">
      <h3 className="text-sm font-semibold">{t('editor.properties')}</h3>

      {/* Todos los valores en MILIMETROS: es la unidad del documento y la que
          entiende el taller. */}
      <div className="grid grid-cols-2 gap-2">
        <Field label={`X (${t('editor.mmUnit')})`}>
          <NumberInput value={layer.transform.e} onChange={(x) => setTransform({ x })} />
        </Field>
        <Field label={`Y (${t('editor.mmUnit')})`}>
          <NumberInput value={layer.transform.f} onChange={(y) => setTransform({ y })} />
        </Field>
        {/*
          MEDIDA EXACTA con candado.

          Con el candado puesto —que es lo normal— escribir el ancho recalcula el
          alto solo. Un logotipo estirado es el defecto mas caro que hay: no se
          nota en pantalla, se nota en la prenda, y para entonces ya esta impreso.
        */}
        <Field label={`${t('editor.size')} X`}>
          <NumberInput value={widthMm} min={1} onChange={(w) => redimensionar(layer.id, w, null)} />
        </Field>
        <Field label={`${t('editor.size')} Y`}>
          <NumberInput value={heightMm} min={1} onChange={(h) => redimensionar(layer.id, null, h)} />
        </Field>
        <Field label={t('editor.rotation')}>
          <NumberInput value={roundMm(rotation)} step={1} onChange={(deg) => setTransform({ deg })} />
        </Field>
        <Field label={t('editor.opacity')}>
          <NumberInput
            value={layer.opacity}
            step={0.05}
            min={0}
            max={1}
            onChange={(opacity) => onChange(layer.id, { opacity } as Partial<Layer>)}
          />
        </Field>
      </div>

      <button
        type="button"
        data-test="candado-proporcion"
        aria-pressed={proporcionBloqueada}
        onClick={() => cambiarProporcionBloqueada(!proporcionBloqueada)}
        className={`toque flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-xs ${
          proporcionBloqueada ? 'border-marca bg-marca/10 text-marca' : 'border-borde text-tinta-suave'
        }`}
      >
        {proporcionBloqueada ? <IconLock size={14} /> : <IconUnlock size={14} />}
        {proporcionBloqueada ? t('editor.ratioLocked') : t('editor.ratioFree')}
      </button>

      {/*
        EL COLOR DE LA TINTA.

        Hasta hoy se fijaba al crear la capa y no se podía cambiar, que es
        justo lo que más se cambia. Y se elige de una CARTA, no de una rueda
        libre: en el taller la tinta se pide por su referencia, y «el azul de mi
        logotipo» necesita un nombre, no una aproximación con el ratón.
      */}
      {(layer.kind === 'text' || layer.kind === 'shape') && (
        <div className="space-y-2 border-t border-borde pt-3">
          <p className="text-xs font-medium">{t('editor.inkColor')}</p>
          <SelectorPantone
            onElegir={(color) =>
              onChange(
                layer.id,
                (layer.kind === 'text'
                  ? { color }
                  : // En una forma, la tinta va al relleno si lo hay y al trazo
                    // si es solo contorno: cambiar el relleno de una línea la
                    // dejaría invisible.
                    layer.fill
                    ? { fill: color }
                    : { stroke: color }) as Partial<Layer>,
              )
            }
          />
        </div>
      )}

      {layer.kind === 'image' && <PanelImagen layer={layer} onChange={onChange} />}

      {layer.kind === 'text' && (
        <>
          <Field label={t('editor.textContent')}>
            <textarea
              value={layer.text}
              onChange={(e) => onChange(layer.id, { text: e.target.value } as Partial<Layer>)}
              rows={2}
              className="w-full rounded-lg border border-borde p-2 text-sm"
            />
          </Field>
          <Field label={`${t('editor.size')} (${t('editor.mmUnit')})`}>
            <NumberInput
              value={layer.sizeMm}
              min={1}
              onChange={(sizeMm) =>
                onChange(layer.id, { sizeMm, lineHeightMm: sizeMm * 1.25 } as Partial<Layer>)
              }
            />
          </Field>

          {/* TIPOGRAFÍA. Agrupada por para qué sirve cada una, no por nombre, y
              con cada opción pintada CON SU PROPIA LETRA y el texto del cliente
              de muestra. Antes era un <select> nativo: elegir «Bangers» por el
              nombre era elegir a ciegas. */}
          <Field label={t('editor.font')}>
            <SelectorFuente
              value={layer.font.family}
              muestra={layer.text}
              etiquetaGrupo={(categoria) => t(`editor.fontGroup.${categoria}`)}
              onChange={(family) => {
                onChange(layer.id, { font: conFuente(layer.font, { family }) } as Partial<Layer>);
              }}
            />
          </Field>

          <Field label={t('editor.weight')}>
            <div className="flex flex-wrap gap-1.5">
              {(buscarFuente(layer.font.family)?.weights ?? [400]).map((w) => (
                <button
                  key={w}
                  data-test="peso"
                  aria-pressed={layer.font.weight === w}
                  onClick={() => onChange(layer.id, { font: conFuente(layer.font, { weight: w }) } as Partial<Layer>)}
                  className={`toque rounded-lg border px-2.5 py-1.5 text-xs ${
                    layer.font.weight === w ? 'border-marca bg-marca text-white' : 'border-borde'
                  }`}
                  style={{ fontWeight: w }}
                >
                  {w === 400 ? t('editor.weightNormal') : w >= 800 ? t('editor.weightBlack') : w >= 600 ? t('editor.weightBold') : w}
                </button>
              ))}
              {buscarFuente(layer.font.family)?.italic && (
                <button
                  data-test="cursiva"
                  aria-pressed={layer.font.italic}
                  onClick={() =>
                    onChange(layer.id, {
                      font: conFuente(layer.font, { italic: !layer.font.italic }),
                    } as Partial<Layer>)
                  }
                  className={`toque rounded-lg border px-2.5 py-1.5 text-xs italic ${
                    layer.font.italic ? 'border-marca bg-marca text-white' : 'border-borde'
                  }`}
                >
                  {t('editor.italic')}
                </button>
              )}
            </div>
          </Field>

          {/*
            EL PAPEL EN LA SERIE DE EQUIPO. Marcar una capa como «nombre» o
            «dorsal» es lo que convierte un diseño en la base de un lote: la
            lista del club sustituye estos textos al validar y al producir.
          */}
          <Field label={t('equipo.rolCampo')}>
            <div className="flex flex-wrap gap-1.5">
              {([undefined, 'nombre', 'dorsal'] as const).map((rol) => (
                <button
                  key={rol ?? 'libre'}
                  data-test={`rol-${rol ?? 'libre'}`}
                  aria-pressed={(layer as { rol?: string }).rol === rol}
                  onClick={() => onChange(layer.id, { rol } as Partial<Layer>)}
                  className={`toque rounded-lg border px-2.5 py-1.5 text-xs ${
                    (layer as { rol?: string }).rol === rol ? 'border-marca bg-marca text-white' : 'border-borde'
                  }`}
                >
                  {rol === undefined ? t('equipo.rolLibre') : rol === 'nombre' ? t('equipo.nombre') : t('equipo.dorsal')}
                </button>
              ))}
            </div>
          </Field>

          <Field label={t('editor.align')}>
            <div className="flex gap-1.5">
              {(['left', 'center', 'right', 'justify'] as const).map((a) => (
                <button
                  key={a}
                  data-test="alineacion"
                  aria-pressed={layer.align === a}
                  onClick={() => onChange(layer.id, { align: a } as Partial<Layer>)}
                  title={t(`editor.align_${a}`)}
                  className={`toque flex-1 rounded-lg border py-1.5 ${
                    layer.align === a ? 'border-marca bg-marca text-white' : 'border-borde'
                  }`}
                >
                  {a === 'left' ? <IconAlignLeft size={16} /> : a === 'center' ? <IconAlignCenter size={16} /> : a === 'right' ? <IconAlignRight size={16} /> : '≡'}
                  <span className="sr-only">{t(`editor.align_${a}`)}</span>
                </button>
              ))}
            </div>
          </Field>

          <Field label={t('editor.textCase')}>
            <div className="flex gap-1.5">
              {(['none', 'upper', 'lower'] as const).map((c) => (
                <button
                  key={c}
                  data-test="mayusculas"
                  aria-pressed={layer.textCase === c}
                  // El texto ORIGINAL no se toca: esto es una transformación
                  // visual, y quien escribió «Peña O Souto» sigue pudiendo
                  // recuperarlo tal cual.
                  onClick={() => onChange(layer.id, { textCase: c } as Partial<Layer>)}
                  className={`toque flex-1 rounded-lg border py-1.5 text-xs ${
                    layer.textCase === c ? 'border-marca bg-marca text-white' : 'border-borde'
                  }`}
                >
                  {t(`editor.case_${c}`)}
                </button>
              ))}
            </div>
          </Field>

          <Field label={t('editor.arc')} hint={t('editor.arcHint')}>
            <div className="flex items-center gap-2">
              {(['none', 'up', 'down'] as const).map((d) => (
                <button
                  key={d}
                  data-test="arco"
                  aria-pressed={d === 'none' ? !layer.arc : layer.arc?.direction === d}
                  onClick={() =>
                    onChange(layer.id, {
                      arc: d === 'none' ? undefined : { radiusMm: layer.arc?.radiusMm ?? 120, direction: d },
                    } as Partial<Layer>)
                  }
                  className={`toque flex-1 rounded-lg border py-1.5 text-xs ${
                    (d === 'none' ? !layer.arc : layer.arc?.direction === d)
                      ? 'border-marca bg-marca text-white'
                      : 'border-borde'
                  }`}
                >
                  {t(`editor.arc_${d}`)}
                </button>
              ))}
            </div>
            {layer.arc && (
              <div className="mt-2">
                <NumberInput
                  value={layer.arc.radiusMm}
                  min={10}
                  onChange={(radiusMm) =>
                    onChange(layer.id, { arc: { ...layer.arc!, radiusMm } } as Partial<Layer>)
                  }
                />
              </div>
            )}
          </Field>

          <Field label={t('editor.outline')} hint={t('editor.outlineHint')}>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                data-test="contorno"
                checked={Boolean(layer.outline)}
                onChange={(e) =>
                  onChange(layer.id, {
                    outline: e.target.checked
                      ? { color: { type: 'rgb', r: 255, g: 255, b: 255 }, widthMm: 0.6 }
                      : undefined,
                  } as Partial<Layer>)
                }
              />
              {layer.outline && (
                <NumberInput
                  value={layer.outline.widthMm}
                  min={0.1}
                  step={0.1}
                  onChange={(widthMm) =>
                    onChange(layer.id, { outline: { ...layer.outline!, widthMm } } as Partial<Layer>)
                  }
                />
              )}
            </div>
          </Field>
        </>
      )}

      <Button variant="secondary" onClick={() => onCenter(layer.id)}>
        {t('editor.center')}
      </Button>
    </Card>
  );
}

/**
 * AVISOS, con su arreglo cuando lo tiene.
 *
 * El diagnóstico ya calcula la medida que falta —«39 ppp, exige 150»— así que
 * ofrecer la corrección es aprovechar un trabajo hecho, y convierte un muro en
 * un paso. No todos se pueden arreglar solos: «demasiadas tintas» exige decidir
 * QUÉ color quitar, y elegir por el cliente sería peor que no ofrecer nada.
 */
function DiagnosticsPanel({
  diagnostics,
  layers,
  zonePolygonMm,
  onFix,
}: {
  diagnostics: Diagnostic[];
  layers?: Layer[];
  zonePolygonMm?: Array<{ x: number; y: number }>;
  onFix?: (layerId: string, patch: Partial<Layer>) => void;
}) {
  const t = useT();
  const errors = diagnostics.filter((d) => d.severity === 'error');
  const warnings = diagnostics.filter((d) => d.severity === 'warning');
  const infos = diagnostics.filter((d) => d.severity === 'info');

  if (diagnostics.length === 0) return <Banner tone="ok" title={t('diagnostics.none')} />;

  return (
    <div className="space-y-2">
      {errors.length > 0 && <Banner tone="error" title={t('diagnostics.errors', { count: errors.length })} />}
      {warnings.length > 0 && <Banner tone="warning" title={t('diagnostics.warnings', { count: warnings.length })} />}
      <ul className="space-y-1.5">
        {[...errors, ...warnings, ...infos].map((d, index) => {
          const capa = d.layerId ? layers?.find((l) => l.id === d.layerId) : undefined;
          const arreglo = capa && zonePolygonMm && onFix ? arregloPara(d, capa, zonePolygonMm) : null;
          return (
            <li
              key={`${d.code}-${d.layerId ?? ''}-${index}`}
              data-test="diagnostico"
              className="rounded-lg border border-borde p-2 text-sm"
            >
              <span className="flex items-start gap-2">
                <Badge tone={d.severity === 'error' ? 'error' : d.severity === 'warning' ? 'warning' : 'neutral'}>
                  {d.severity}
                </Badge>
                <span className="min-w-0">
                  {/* El texto sale del CODIGO estable, no del servidor: asi se
                      traduce sin depender del idioma en el que se valido. */}
                  {t(`diagnostics.codes.${d.code}`, {
                    measured: d.measured ?? '',
                    expected: d.expected ?? '',
                    ...(d.params ?? {}),
                  })}
                  {d.params?.estimated ? (
                    <em className="ml-1 text-xs text-tinta-suave">({t('diagnostics.estimated')})</em>
                  ) : null}
                </span>
              </span>
              {arreglo && capa && (
                <button
                  type="button"
                  data-test="arreglar"
                  onClick={() => onFix?.(capa.id, arreglo.parche)}
                  className="toque mt-1.5 w-full rounded-lg border border-marca px-2 py-1.5 text-xs text-marca"
                >
                  {t(`editor.${arreglo.clave.replace('fix.', 'fix_')}`, { valor: arreglo.valor ?? '' })}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * CUÁNTAS TINTAS LLEVA Y QUÉ CUESTAN.
 *
 * En serigrafía cada color es una pantalla, y cada pantalla un coste fijo que
 * no baja por hacer más prendas. Que el cliente lo vea mientras decide es lo
 * que evita la conversación incómoda al final.
 */
/**
 * «Esto no se va a ver»: capas casi del color de la prenda elegida.
 *
 * Orientativo a propósito —ni bloquea guardar ni comprar—: la validación
 * isomorfa no conoce el color de la prenda y no se toca ese contrato por un
 * aviso. El arreglo de un toque va por el mismo camino que los diagnósticos.
 */
function AvisoContraste({
  layers,
  prenda,
  onFix,
}: {
  layers: Layer[];
  prenda: { r: number; g: number; b: number };
  onFix: (layerId: string, patch: Partial<Layer>) => void;
}) {
  const t = useT();
  const avisos = avisosDeContraste(layers, prenda);
  if (avisos.length === 0) return null;
  return (
    <div data-test="aviso-contraste" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
      <p className="font-medium">{t('editor.contrastTitle')}</p>
      <p className="mt-1 text-xs text-tinta-suave">{t('editor.contrastBody')}</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {avisos.map((a) => (
          <button
            key={a.layerId}
            data-test="arreglar-contraste"
            onClick={() => onFix(a.layerId, a.arreglo)}
            className="toque rounded-lg border border-borde bg-white px-2.5 py-1.5 text-xs"
          >
            {t('editor.contrastFix')}
          </button>
        ))}
      </div>
    </div>
  );
}

function PanelTintas({
  layers,
  prendaOscura,
  tecnica,
  maximoTintas,
}: {
  layers: Layer[];
  prendaOscura: boolean;
  tecnica: string;
  maximoTintas: number;
}) {
  const t = useT();
  const tintas = contarTintas(layers);
  if (tintas.length === 0) return null;

  const esSerigrafia = tecnica === 'screen_print';
  const conBase = necesitaBaseBlanca(tintas, prendaOscura);
  const pantallas = pantallasDeSerigrafia(tintas, conBase);

  return (
    <Card className="space-y-2">
      <h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
        {t('editor.inks', { n: tintas.length })}
        {esSerigrafia && tintas.length > maximoTintas && (
          <Badge tone="error">{t('editor.tooManyInks', { max: maximoTintas })}</Badge>
        )}
      </h3>

      <ul className="flex flex-wrap gap-1.5">
        {tintas.map((tinta) => (
          <li
            key={tinta.clave}
            data-test="tinta"
            className="flex items-center gap-1 rounded-full border border-borde px-2 py-1"
          >
            <span
              className="h-3.5 w-3.5 rounded-full border border-borde"
              style={{ backgroundColor: `rgb(${tinta.rgb.r},${tinta.rgb.g},${tinta.rgb.b})` }}
              aria-hidden="true"
            />
            <span className="text-[11px]">{tinta.etiqueta}</span>
          </li>
        ))}
      </ul>

      {esSerigrafia && <p className="text-xs text-tinta-suave">{t('editor.screens', { n: pantallas })}</p>}

      {conBase && (
        <p data-test="base-blanca" className="rounded-lg bg-fondo-suave p-2 text-xs">
          {/* El modelo ya tenía el color «blanco técnico» y el aviso
              TECHNICAL_WHITE_NOT_AVAILABLE: lo que faltaba era ENSEÑARLO. */}
          {t('editor.whiteBaseNeeded')}
        </p>
      )}
    </Card>
  );
}

/**
 * CARTA DE TINTAS.
 *
 * Un cliente que pide «el azul de mi logotipo» necesita NOMBRARLO, no
 * aproximarlo con una rueda de color: en el taller la tinta se pide por
 * referencia. Las vistas previas son aproximadas y lo dice el pie: una pantalla
 * no reproduce una tinta plana.
 */
function SelectorPantone({ onElegir }: { onElegir: (color: Color) => void }) {
  const t = useT();
  return (
    <div className="space-y-2">
      <ul className="grid grid-cols-4 gap-1.5">
        {CARTA_PANTONE.map((entrada) => (
          <li key={entrada.code}>
            <button
              type="button"
              data-test="pantone"
              onClick={() => {
                const color = colorPantone(entrada.code);
                if (color) onElegir(color);
              }}
              className="toque flex w-full flex-col items-center gap-1 rounded-lg border border-borde p-1.5"
            >
              <span
                className="h-8 w-full rounded border border-borde"
                style={{ backgroundColor: `rgb(${entrada.rgb.r},${entrada.rgb.g},${entrada.rgb.b})` }}
                aria-hidden="true"
              />
              <span className="text-[10px] leading-tight">{entrada.code}</span>
            </button>
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-tinta-suave">{t('editor.pantoneNote')}</p>
    </div>
  );
}
